"use client";

import { isNavHrefActive, matchesAppAlias } from "@/lib/vcards/vcardsApp";
import { useState, useRef, useEffect, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useChatNotifications } from "@/contexts/ChatNotificationsContext";
import {
  GAM_SLASH_MENU_EVENT,
  GAM_SLASH_DISMISS_EVENT,
  dispatchSlashDismiss,
  PANEL_RECENTS,
  readRecents,
  pushRecent,
  readSlashOrder,
  writeSlashOrder,
  resetSlashOrder,
  orderCategories,
  orderItems,
  moveCategory,
  moveItem,
  type GamSlashMenuEventDetail,
  type SlashOrder,
} from "@gam/editor";
import {
  LayoutDashboard,
  ShieldAlert,
  Activity,
  HeartPulse,
  Inbox,
  Layers,
  Map,
  Grid3X3,
  CalendarDays,
  Bot,
  Palette,
  Network,
  Calendar,
  Compass,
  Fingerprint,
  Users,
  Zap,
  Rss,
  MessagesSquare,
  MessageCircle,
  Upload,
  Sparkles,
  Settings,
  Shield,
  X,
  List,
  LayoutGrid,
  AlignJustify,
  Star,
  Download,
  Database,
  BookOpen,
  Sheet,
  Presentation,
  Globe,
  User as UserIcon,
  CircleDot,
  FileSignature,
  ClipboardList,
  Mail,
  ChevronRight,
  Gauge,
  FolderOpen,
  ListTree,
  Milestone,
  Pencil,
  Wrench,
  Check,
  EyeOff,
  Eye,
  GripVertical,
  RotateCcw,
  ChevronUp,
  ChevronDown,
  BarChart3,
  Plus,
  Trash2,
  Cpu,
  Cloud,
  FolderArchive,
  Bookmark,
  BookMarked,
  Bell,
  BookOpenText,
  ScrollText,
  Library,
  FlaskConical,
  Lightbulb,
  DatabaseZap,
  Lock,
  KeyRound,
  ShieldCheck,
  GraduationCap,
  Store,
  BrainCircuit,
  Brain,
  FileStack,
  Phone,
  MessageSquareCode,
  MessageSquare,
  Plug,
  Cable,
  RefreshCw,
  Image,
  Video,
  AudioLines,
  UserCircle,
  UserSearch,
  Film,
  Search,
  Clock,
  Monitor,
  Code2,
  FolderTree,
  HardHat,
  Wallet,
  Wind,
  Newspaper,
  CheckCircle2,
  Puzzle,
  PanelLeft,
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, rectSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useQueryClient } from "@tanstack/react-query";
import { SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH } from "@/lib/hooks/useShellPrefs";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import { getTranslations } from "@/lib/i18n";
import { useBreakpoint } from "@/lib/hooks/useBreakpoint";
import { loadFavorites, saveFavorites } from "./widgets/FavoritesWidget";
import { useSidebarCustomization } from "@/lib/sidebar/useSidebarCustomization";
import { buildDisplayGroups } from "@/lib/sidebar/sidebarCustomization";
import { useAppsRegistry, getAppStatusByRoute, getStatusBadge, type AppRecord } from "@/lib/hooks/useAppsRegistry";
import { useRecentItems, deleteRecentItem } from "@/lib/hooks/useRecentItems";
import { SidebarContextMenu } from "./SidebarContextMenu";
import { ItemEditPopover, getIconByName } from "./ItemEditPopover";
import { SOCIAL_LINKS, SocialIcon } from "./DownloadReminder";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { SidebarWidgetInspector } from "./SidebarWidgetInspector";
import { FolderStrip } from "./FolderStrip";
import { useAppFolders, buildMemberKeySet } from "@/lib/hooks/useAppFolders";
import { usePlatformFilter, ALL_PLATFORMS } from "@/lib/platforms/usePlatformFilter";
import { PlatformSelector } from "@/components/platforms/PlatformSelector";
import { useFamilyFilter, ALL_FAMILIES } from "@/lib/families/useFamilyFilter";
import { FamilySelector } from "@/components/families/FamilySelector";
import { useSelectedAppFolder } from "@/lib/sidebar/useSelectedAppFolder";
import { useSidebarScope, type SidebarScope } from "@/lib/sidebar/useSidebarScope";
import { SidebarScopeToggle } from "./SidebarScopeToggle";
import { SidebarClassicNav, type ClassicEntry, type ClassicSection } from "./SidebarClassicNav";

// Per-app gradient map — moved to src/lib/app-launcher/app-gradients.ts
// (Sprint C.3) so AppLauncherItem can share the same color identity.
// User overrides come through userAppGradients (localStorage layer).
import { getEffectiveGradient, useUserAppGradientsRev } from "@/lib/app-launcher/userAppGradients";
import { shellChromeHeight, shellChromeTop } from "@/lib/shellChromeOffsets";

const APPS_VIEW_WIDTH = 64;

const FULL_WIDTH = 240;
const MOBILE_WIDTH = 280;
const STRIP_WIDTH = 60;

const FILTER_KEY = "cc-sidebar-filter";
const VIEW_MODE_KEY = "cc-sidebar-view-mode";
// One-time switch to classic for readers who already had a stored mode.
// Without it "default" would only reach browsers that never picked a mode —
// which is nobody who has used the sidebar, Gal included.
const CLASSIC_DEFAULT_KEY = "cc-sidebar-classic-default-v1";
const FOLDER_MODE_KEY = "cc-sidebar-folder-mode";
// Selected app-folder/tag is persisted via the useSelectedAppFolder hook
// (storage key "cc-sidebar-app-folder-filter"). The constant used to live here.

// ─── Types ──────────────────────────────────────────────

type SidebarFilter = "me" | "team" | "hidden" | "favorites";
// "classic" = the Notion/ClickUp/Fibery tree (SidebarClassicNav). Default since 14.09.
type ViewMode = "list" | "grid" | "compact" | "apps" | "classic";

// ─── Grouped Navigation ─────────────────────────────────

export interface NavItem {
  href: string;
  key: string;
  icon: React.ElementType;
  status: "active" | "coming-soon";
}

export interface NavFolder {
  type: "folder";
  href: string;
  key: string;
  icon: React.ElementType;
  status: "active" | "coming-soon";
  children: NavItem[];
}

export type NavEntry = NavItem | NavFolder;

function isFolder(entry: NavEntry): entry is NavFolder {
  return "type" in entry && entry.type === "folder";
}

/** Tiny status badge from vb_apps DB — shows "בפיתוח" / "מתוכנן" / "רעיון" */
function AppStatusBadge({ href, allApps }: { href: string; allApps: AppRecord[] }) {
  const status = getAppStatusByRoute(allApps, href);
  if (!status) return null;
  const label = getStatusBadge(status);
  if (!label) return null;
  const colors = status === "in-progress"
    ? "text-blue-400 bg-blue-400/10"
    : status === "idea"
      ? "text-slate-500 bg-slate-500/10"
      : "text-amber-400 bg-amber-400/10";
  return (
    <span className={`shrink-0 rounded px-1 py-0 text-[0.5rem] font-medium leading-tight ${colors}`}>
      {label}
    </span>
  );
}

/** Unread counter for the internal chat row only — renders nothing on every other row.
    This is the answer to the real risk of moving alerts off WhatsApp: a phone rings, a web
    page is silent. Pairs with the sound + browser notification in ChatNotificationsContext. */
function ChatUnreadBadge({ href }: { href: string }) {
  const { totalUnread } = useChatNotifications();
  if (href !== "/dashboard/chat" || totalUnread === 0) return null;
  return (
    <span className="shrink-0 rounded-full bg-teal-500 px-1.5 text-[0.5625rem] font-bold leading-4 text-slate-950">
      {totalUnread > 99 ? "99+" : totalUnread}
    </span>
  );
}

// ─── Sortable Section Wrapper ─────────────────────────────

function SortableSectionDiv({ id, editMode, children }: { id: string; editMode: boolean; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !editMode,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...(editMode ? listeners : {})} className={editMode ? "cursor-grab active:cursor-grabbing" : ""}>
      {children}
    </div>
  );
}

// ─── Sortable Wrapper ────────────────────────────────────

function SortableNavItem({
  id,
  editMode,
  isHidden,
  onToggleHide,
  onRight,
  onEditClick,
  children,
}: {
  id: string;
  editMode: boolean;
  isHidden: boolean;
  onToggleHide: () => void;
  onRight: boolean;
  onEditClick?: () => void;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !editMode,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : isHidden ? 0.3 : 1,
  };

  if (!editMode) return <>{children}</>;

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="group/sortable relative">
      <div
        {...listeners}
        className={`absolute top-1/2 -translate-y-1/2 z-30 cursor-grab active:cursor-grabbing p-0.5 rounded text-slate-600 hover:text-slate-400 transition-colors ${
          onRight ? "right-0" : "left-0"
        }`}
      >
        <GripVertical className="h-3 w-3" />
      </div>
      <div
        className={onRight ? "pr-5" : "pl-5"}
        onClick={(e) => {
          if (onEditClick) {
            e.preventDefault();
            e.stopPropagation();
            onEditClick();
          }
        }}
        style={{ cursor: onEditClick ? "pointer" : undefined }}
      >
        {children}
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onToggleHide(); }}
        className={`absolute top-1/2 -translate-y-1/2 z-30 rounded p-0.5 transition-all ${
          onRight ? "left-1" : "right-1"
        } ${
          isHidden
            ? "text-amber-400 opacity-100"
            : "text-slate-600 opacity-0 group-hover/sortable:opacity-100 hover:text-slate-400"
        }`}
      >
        {isHidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
      </button>
    </div>
  );
}

export interface NavGroup {
  id: string;
  labelKey: string;
  items: NavEntry[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    id: "core",
    labelKey: "groupCore",
    items: [
      { href: "/dashboard", key: "dashboard", icon: LayoutDashboard, status: "active" },
      { href: "/dashboard/news", key: "news", icon: Newspaper, status: "active" },
      { href: "/dashboard/app-launcher", key: "appLauncher", icon: LayoutGrid, status: "active" },
      { href: "/dashboard/layers", key: "layers", icon: Activity, status: "active" },
      // vCards = Editor + Entities merged (Gal 19.09); both old names live on as search aliases.
      { href: "/dashboard/editor", key: "vcards", icon: Database, status: "active" },
      { href: "/dashboard/story-map", key: "storyMap", icon: Map, status: "active" },
      { href: "/dashboard/pinkas", key: "notebook", icon: BookOpen, status: "active" },
      { href: "/dashboard/memory", key: "memory", icon: Library, status: "active" },
      // Internal chat — system alerts land here (alongside WhatsApp, not instead of it).
      { href: "/dashboard/chat", key: "chat", icon: MessageSquare, status: "active" },
      { href: "/dashboard/os-map", key: "osMap", icon: ListTree, status: "active" },
      { href: "/dashboard/virtual-office", key: "virtualOffice", icon: Network, status: "active" },
      // ⚫ Black Office's surface — the project roadmap over black_spine_index.
      // Sits next to os-map because the two are the pair of "where things stand"
      // maps: os-map is the structure, this is the sequence of gates through it.
      // NOT /dashboard/roadmap — that is the pm_* vTask shell, a different App.
      { href: "/dashboard/roadmap-black", key: "roadmapBlack", icon: Milestone, status: "active" },
      { href: "/dashboard/specs", key: "specs", icon: BookOpenText, status: "active" },
      {
        type: "folder",
        href: "/dashboard/ai-hub",
        key: "aiHub",
        icon: Bot,
        status: "active",
        children: [
          { href: "/dashboard/ai-home", key: "aiHome", icon: Sparkles, status: "active" },
          { href: "/dashboard/ai-hub", key: "cliAi", icon: Bot, status: "active" },
          { href: "/dashboard/advisors", key: "aiAdvisors", icon: Users, status: "active" },
          { href: "/dashboard/boardroom", key: "boardroom", icon: Users, status: "active" },
          { href: "/dashboard/agents", key: "agents", icon: Cpu, status: "active" },
          { href: "/dashboard/ai-knowledge", key: "aiKnowledge", icon: BookMarked, status: "coming-soon" },
          { href: "/dashboard/brain", key: "semanticBrain", icon: BrainCircuit, status: "active" },
        ],
      },
      { href: "/dashboard/notion-order", key: "notionOrder", icon: List, status: "active" },
      // DEC-0045: library per system field — vTask's pool (cross-platform,
      // every instance that has the field activated). First view: tasks list.
      { href: "/dashboard/vtask-pool", key: "vtaskPool", icon: CheckCircle2, status: "active" },
      // "תצוגות" merged into /dashboard/entities on 2026-08-03 — it listed the
      // same sources and linked to the same destination. Two sidebar entries
      // for one place is the duplication the merge removes; the route itself
      // still redirects, so old links keep working.
      { href: "/dashboard/pinkas-100", key: "pinkas100", icon: HardHat, status: "active" },
      { href: "/dashboard/pinkas-kablanim", key: "pinkasKablanim", icon: HardHat, status: "active" },
      { href: "/dashboard/investigator", key: "investigator", icon: Fingerprint, status: "active" },
      { href: "/dashboard/memory-catalog", key: "memoryCatalog", icon: Library, status: "active" },
      // Full-screen half of the Bookmarks app; the drawer in the shell is the
      // other half. Both read the same vcollections rows.
      // Key is bookmarksApp, not bookmarks — the latter is already taken by the
      // feeds folder further down. Two different things, two different keys.
      { href: "/dashboard/bookmarks", key: "bookmarksApp", icon: Bookmark, status: "active" },
      { href: "/dashboard/ideas", key: "ideaBacklog", icon: Lightbulb, status: "active" },
      // Reference App (מילוני מערכת) folded into Entities 2026-08-01 — it is
      // the "קטלוג" registry_kind, reached via the entities home page's tabs
      // (migration 20260801100000_entity_types_registry_kind.sql), not a
      // separate top-level nav item. /dashboard/reference still redirects there.
      // vTool — Internal Tools meta-platform. Sits next to "entities" because the
      // two are parallel top layers: entities = the schema/definition surface,
      // vTool = the runtime tools surface over /internal-tools.
      // Registered in src/lib/internal-tools/data-sources/nav-items.ts; Phase B
      // will make the Sidebar consume that registry instead of this hardcoded entry.
      { href: "/internal-tools", key: "vTool", icon: Wrench, status: "active" },
      {
        // מאגר הידע הוא תיקייה משלוש שנים: המאגר עצמו, ספריית המחקר
        // ומדף המקורות. שלושתם קוראים מ-knowledge_items, בעדשות שונות.
        type: "folder",
        href: "/dashboard/knowledge",
        key: "knowledge",
        icon: Brain,
        status: "active",
        children: [
          { href: "/dashboard/knowledge/sources", key: "knowledgeSources", icon: Library, status: "active" },
          { href: "/dashboard/knowledge/research", key: "researchLibrary", icon: FlaskConical, status: "active" },
        ],
      },
      { href: "/dashboard/playbooks", key: "playbooks", icon: ScrollText, status: "active" },
      { href: "/dashboard/comms", key: "comms", icon: MessagesSquare, status: "active" },
      { href: "/dashboard/whatsapp", key: "whatsapp", icon: MessageCircle, status: "active" },
      { href: "/dashboard/cost", key: "cost", icon: Wallet, status: "active" },
      { href: "/dashboard/documents", key: "documents", icon: FileSignature, status: "active" },
      { href: "/dashboard/vclip", key: "vclip", icon: Film, status: "active" },
      { href: "/dashboard/vcanvas", key: "vcanvas", icon: Pencil, status: "active" },
      { href: "/dashboard/weekly-planner", key: "weeklyPlanner", icon: CalendarDays, status: "active" },
      {
        // Was the "Wiki פרטי" folder. /dashboard/wiki is retired (redirects to
        // /dashboard/knowledge, which already has its own entry above) — so this
        // folder is now what its remaining children always were: Claude Stack.
        type: "folder",
        href: "/dashboard/skills",
        key: "skills",
        icon: GraduationCap,
        status: "active",
        children: [],
      },
      {
        type: "folder",
        href: "/dashboard/vcloud",
        key: "vcloud",
        icon: Cloud,
        status: "active",
        children: [
          { href: "/dashboard/vcloud/files", key: "vcloudFiles", icon: FolderArchive, status: "active" },
          { href: "/dashboard/vcloud/images", key: "vcloudImages", icon: Image, status: "active" },
          { href: "/dashboard/vcloud/video", key: "vcloudVideo", icon: Video, status: "active" },
          { href: "/dashboard/vcloud/sound", key: "vcloudSound", icon: AudioLines, status: "active" },
          { href: "/dashboard/vcloud/personal", key: "vcloudPersonal", icon: UserCircle, status: "active" },
        ],
      },
    ],
  },
  {
    id: "tools",
    labelKey: "groupTools",
    items: [
      { href: "/dashboard/functional-map", key: "functionalMap", icon: Grid3X3, status: "active" },
      { href: "/dashboard/budget", key: "budget", icon: Wallet, status: "active" },
      {
        type: "folder",
        href: "/dashboard/design-system",
        key: "libraries",
        icon: Library,
        status: "active",
        children: [
          { href: "/dashboard/entities/fields", key: "entityFields", icon: ListTree, status: "active" },
          { href: "/dashboard/design-system", key: "designSystem", icon: Palette, status: "active" },
          { href: "/dashboard/entities/field-templates", key: "fieldTemplates", icon: Layers, status: "coming-soon" },
          { href: "/dashboard/icon-library", key: "iconLibrary", icon: Sparkles, status: "active" },
          { href: "/dashboard/vcloud?tab=courses", key: "courses", icon: GraduationCap, status: "active" },
        ],
      },
      {
        type: "folder",
        href: "/dashboard/grid",
        key: "databases",
        icon: DatabaseZap,
        status: "active",
        children: [
          { href: "/dashboard/grid", key: "grid", icon: Sheet, status: "active" },
          // משטח אחד לכל ההתאמות. המנוע האמיתי הוא טאב בפנים, לא מסלול נפרד —
          // אותם נתונים, והעדשה היא מה שמשתנה (02.08.2026).
          { href: "/dashboard/matching", key: "matching", icon: Sparkles, status: "active" },
          { href: "/dashboard/talent-search", key: "talentSearch", icon: UserSearch, status: "active" },
        ],
      },
      { href: "/dashboard/slides", key: "slides", icon: Presentation, status: "active" },
      { href: "/dashboard/toolkit", key: "toolkit", icon: Wrench, status: "active" },
      { href: "/dashboard/windmill", key: "windmill", icon: Wind, status: "active" },
      { href: "/dashboard/tools", key: "embeddedTools", icon: Puzzle, status: "active" },
      {
        type: "folder",
        href: "/dashboard/email-templates",
        key: "templates",
        icon: FileStack,
        status: "active",
        children: [
          { href: "/dashboard/email-templates", key: "emailTemplates", icon: Mail, status: "active" },
          { href: "/dashboard/phone-templates", key: "phoneTemplates", icon: Phone, status: "coming-soon" },
          { href: "/dashboard/prompts", key: "prompts", icon: MessageSquareCode, status: "coming-soon" },
        ],
      },
      {
        type: "folder",
        href: "/dashboard/integrations",
        key: "integrations",
        icon: Plug,
        status: "active",
        children: [
          { href: "/dashboard/integrations", key: "myConnections", icon: Cable, status: "active" },
          { href: "/dashboard/integrations/store", key: "appStore", icon: Store, status: "active" },
          { href: "/dashboard/sync", key: "sync", icon: RefreshCw, status: "active" },
        ],
      },
      {
        type: "folder",
        href: "/dashboard/vault",
        key: "vault",
        icon: Lock,
        status: "coming-soon",
        children: [
          { href: "/dashboard/vault/passwords", key: "vaultPasswords", icon: KeyRound, status: "coming-soon" },
          { href: "/dashboard/vault/secrets", key: "vaultSecrets", icon: ShieldCheck, status: "coming-soon" },
        ],
      },
    ],
  },
  {
    id: "system",
    labelKey: "groupSystem",
    items: [
      {
        type: "folder",
        href: "/dashboard/control",
        key: "control",
        icon: Gauge,
        status: "active",
        children: [
          { href: "/dashboard/roadmap", key: "roadmap", icon: Compass, status: "active" },
          { href: "/dashboard/plan", key: "plan", icon: Calendar, status: "active" },
          { href: "/dashboard/architecture", key: "architecture", icon: Network, status: "active" },
          { href: "/dashboard/admin", key: "admin", icon: Shield, status: "active" },
          { href: "/dashboard/admin/data-brain", key: "adminDataBrain", icon: DatabaseZap, status: "active" },
          { href: "/dashboard/health", key: "health", icon: HeartPulse, status: "active" },
          { href: "/dashboard/system-cleanup", key: "systemCleanup", icon: Sparkles, status: "active" },
          { href: "/dashboard/server-health", key: "serverHealth", icon: Monitor, status: "active" },
          { href: "/dashboard/system-monitoring", key: "systemMonitoring", icon: BarChart3, status: "active" },
          { href: "/dashboard/integration-health", key: "integrationHealth", icon: Activity, status: "active" },
          // Sits with the health surfaces because it answers the same shape of
          // question — is this still alive? — for the security tripwires.
          { href: "/dashboard/honeypot", key: "honeypot", icon: ShieldAlert, status: "active" },
          { href: "/dashboard/maintenance", key: "maintenance", icon: Wrench, status: "active" },
          { href: "/dashboard/audit", key: "audit", icon: ClipboardList, status: "active" },
          { href: "/dashboard/db-explorer", key: "dbExplorer", icon: FolderTree, status: "active" },
          // Sits next to db-explorer on purpose: same subject (the database),
          // different question. Explorer = where a table sits in the taxonomy;
          // registry = what the table is and how to use it correctly (DEC-0269).
          { href: "/dashboard/db-registry", key: "dbRegistry", icon: Database, status: "active" },
          { href: "/dashboard/identity-resolver", key: "identityResolver", icon: Fingerprint, status: "active" },
          // Sits beside the identity resolver: same subject (who is this),
          // different question. Resolver = which business is this row about;
          // agent access = which agent is holding a key right now (DEC-0371).
          { href: "/dashboard/agent-access", key: "agentAccess", icon: KeyRound, status: "active" },
        ],
      },
      { href: "/dashboard/import", key: "import", icon: Upload, status: "active" },
      {
        type: "folder",
        href: "/dashboard/feeds",
        key: "bookmarks",
        icon: Bookmark,
        status: "active",
        children: [
          { href: "/dashboard/feeds", key: "feeds", icon: Rss, status: "active" },
          { href: "/dashboard/read-list", key: "readList", icon: BookMarked, status: "coming-soon" },
        ],
      },
      { href: "/dashboard/automations-live", key: "automations", icon: Zap, status: "active" },
      { href: "/dashboard/alerts", key: "alerts", icon: Bell, status: "active" },
      { href: "/dashboard/pm", key: "vdev", icon: Code2, status: "active" },
      { href: "/dashboard/task-inbox", key: "taskInbox", icon: Inbox, status: "active" },
      { href: "/dashboard/settings", key: "settings", icon: Settings, status: "active" },
    ],
  },
];

// ─── Helpers ─────────────────────────────────────────────

function loadFilter(): SidebarFilter {
  try {
    const v = localStorage.getItem(FILTER_KEY);
    if (v === "me" || v === "team" || v === "hidden" || v === "favorites") return v;
  } catch {
    // Ignore localStorage errors - use default filter
  }
  return "me";
}

function loadViewMode(): ViewMode {
  try {
    if (!localStorage.getItem(CLASSIC_DEFAULT_KEY)) {
      localStorage.setItem(CLASSIC_DEFAULT_KEY, "1");
      localStorage.setItem(VIEW_MODE_KEY, "classic");
      return "classic";
    }
    const v = localStorage.getItem(VIEW_MODE_KEY);
    if (v === "list" || v === "grid" || v === "compact" || v === "apps" || v === "classic") return v;
  } catch {
    // Ignore localStorage errors - use default view mode
  }
  return "classic";
}

// ─── Component ───────────────────────────────────────────

interface SidebarProps {
  /** Effective visibility mode (accounts for mobile/tablet overrides) */
  effectiveMode?: "visible" | "float" | "hidden";
  isFloating?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  /** Custom width from ShellPrefs (replaces FULL_WIDTH) */
  customWidth?: number;
  /** When true, sidebar collapses to strip and expands on hover (for "visible" mode) */
  sidebarHoverMode?: boolean;
  /** When true, sidebar stays expanded and pushes content */
  sidebarPinned?: boolean;
  /** Toggle pin state */
  onPinToggle?: () => void;
  /** Callback when sidebar width changes via drag resize */
  onWidthChange?: (width: number) => void;
  /** Pixel offset from the active edge (left or right based on sidebarPosition).
   *  Used when an outer panel (e.g. SidebarFoldersDrawer) sits flush against
   *  the same viewport edge and needs the sidebar pushed inward. Default 0. */
  edgeOffset?: number;
}

export function Sidebar({
  effectiveMode,
  isFloating = false,
  isOpen = true,
  onClose,
  customWidth,
  sidebarHoverMode = false,
  sidebarPinned = false,
  onPinToggle: _onPinToggle,
  onWidthChange,
  edgeOffset = 0,
}: SidebarProps = {}) {
  const pathname = usePathname();
  // Subscribe to user gradient overrides so tiles re-paint when the user
  // changes a color from the Icon Library hub.
  useUserAppGradientsRev();

  // ── Top-level sidebar mode (3 tabs at the head of the expanded sidebar) ──
  // 1 = current default nav (existing behavior)
  // 3 = widget settings drill-down (will show miller-columns of the currently-
  //     open widget's settings — like macOS Finder columns)
  // (Tab 2 was "extended folders" — removed 2026-05-17 when folders moved to
  //  the far-right FoldersDrawer chrome surface. Old localStorage value 2
  //  is sanitized to 1 below.)
  const [topTab, setTopTab] = useState<1 | 3>(1);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem("cc-sidebar-top-tab");
      if (raw === "1") {
        setTopTab(1);
      } else if (raw === "3") {
        setTopTab(3);
      } else if (raw === "2") {
        // Sanitize legacy value — Tab 2 no longer exists.
        setTopTab(1);
        localStorage.setItem("cc-sidebar-top-tab", "1");
      }
    } catch { /* ignore */ }
  }, []);
  const changeTopTab = useCallback((next: 1 | 3) => {
    setTopTab(next);
    try { localStorage.setItem("cc-sidebar-top-tab", String(next)); } catch { /* ignore */ }
  }, []);
  const { language, sidebarPosition, sidebarVisibility, brandProfile: _brandProfile } = useSettings();
  const { user, signOut: _signOut, permissions } = useAuth();
  const t = getTranslations(language);
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";
  const { allApps } = useAppsRegistry();
  const { recentItems } = useRecentItems();
  const queryClient = useQueryClient();
  const [hovered, setHovered] = useState(false);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  // Slash-menu → Sidebar bridge (packages/editor can't import this component,
  // so it announces state on `window` — see GAM_SLASH_MENU_EVENT). While a
  // Tiptap slash command is active anywhere on the page, the Apps nav slides
  // out and this mirrors the full catalog the thin quick bar only samples.
  const [slashMenu, setSlashMenu] = useState<GamSlashMenuEventDetail | null>(null);
  useEffect(() => {
    // Opening only. `open: false` from the extension is NOT a close — the
    // suggestion plugin tears itself down on its own about 100ms after
    // opening, and honouring that is exactly why this panel never appeared:
    // it slid out and was gone again before a frame was painted. The quick
    // bar learned this in ed7949510 and survived; this listener did not, and
    // the whole 27-plugin catalog stayed unreachable behind it.
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<GamSlashMenuEventDetail>).detail;
      if (!detail?.open) return;
      setSlashMenu(detail);
    };
    // Closing is this event's job alone — Escape, a pick, or a click outside,
    // announced by whichever surface the user actually acted on.
    const dismiss = () => setSlashMenu(null);
    window.addEventListener(GAM_SLASH_MENU_EVENT, handler);
    window.addEventListener(GAM_SLASH_DISMISS_EVENT, dismiss);
    return () => {
      window.removeEventListener(GAM_SLASH_MENU_EVENT, handler);
      window.removeEventListener(GAM_SLASH_DISMISS_EVENT, dismiss);
    };
  }, []);

  // תוספים panel — the user's own order for the families and for the plugins
  // inside them, plus the last few plugins actually used. Both live in
  // localStorage; both are hydrated after mount (never during render) so the
  // server render and the first client render agree.
  const [slashOrder, setSlashOrder] = useState<SlashOrder>({ categories: [], items: {} });
  const [slashRecents, setSlashRecents] = useState<string[]>([]);
  useEffect(() => {
    setSlashOrder(readSlashOrder());
    setSlashRecents(readRecents(PANEL_RECENTS));
  }, []);

  // One entry point for running a plugin from the panel, so "last used" is
  // recorded no matter which of the two lists — the quick strip or the full
  // catalog — the click came from. Before this, only the floating quick bar
  // recorded anything, so the strip would have stayed empty for anyone who
  // works from the sidebar.
  type SlashItem = Parameters<NonNullable<GamSlashMenuEventDetail["select"]>>[0];
  const runSlashItem = useCallback(
    (item: SlashItem) => {
      setSlashRecents(pushRecent(item.title, PANEL_RECENTS));
      slashMenu?.select?.(item);
      // Announce rather than just closing locally: the quick bar is showing
      // the same slash and has to come down with the panel.
      dispatchSlashDismiss();
    },
    [slashMenu]
  );

  const moveSlashCategory = useCallback((visible: string[], name: string, dir: -1 | 1) => {
    setSlashOrder((prev) => writeSlashOrder(moveCategory(prev, visible, name, dir)));
  }, []);

  const moveSlashItem = useCallback(
    (categoryName: string, visible: string[], title: string, dir: -1 | 1) => {
      setSlashOrder((prev) => writeSlashOrder(moveItem(prev, categoryName, visible, title, dir)));
    },
    []
  );

  // userMenuOpen removed — handled by WorkspaceSwitcher
  const [socialMenuOpen, setSocialMenuOpen] = useState(false);
  // userMenuRef removed — handled by WorkspaceSwitcher
  const socialMenuRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const [navTop, setNavTop] = useState(120);
  const [indicatorStyle, setIndicatorStyle] = useState<{ top: number; height: number } | null>(null);

  const [filter, setFilter] = useState<SidebarFilter>("me");
  const [storedViewMode, setViewMode] = useState<ViewMode>("classic");
  const [searchQuery, setSearchQuery] = useState("");
  const [favHrefs, setFavHrefs] = useState<Set<string>>(new Set());
  const [openFolders, setOpenFolders] = useState<Set<string>>(new Set<string>());
  // Selected tag/folder lives in the shared hook so SidebarFoldersDrawer
  // (rendered by DashboardShell, outside this component) and FolderStrip
  // (rendered inline below) stay in sync. Persisted to localStorage
  // "cc-sidebar-app-folder-filter".
  const { selectedId: selectedAppFolderId, setSelectedId: setSelectedAppFolderId } = useSelectedAppFolder();
  const { folders: appFolders } = useAppFolders();
  // Platform filter — narrows the app menu to one domain of work (DEC-0362).
  // Sits alongside the folder filter rather than replacing it: a folder is a
  // hand-made grouping, a platform is the App's declared home in the database.
  const platformFilter = usePlatformFilter();
  // Family filter — the second axis: what KIND of thing this is (a pool, a
  // library, an entity, settings, agents…), independent of which domain of
  // work it serves. Two questions, two rows; neither answers the other.
  const familyFilter = useFamilyFilter();
  // Scope — which of those three slices is on screen (2026-09-06). They used
  // to be three permanent rows; now one toggle picks one.
  const { scope, setScope } = useSidebarScope();

  // Hydrate openFolders from localStorage after mount to avoid SSR mismatch
  useEffect(() => {
    try {
      const v = localStorage.getItem("cc-sidebar-folders");
      if (v) setOpenFolders(new Set(JSON.parse(v)));
    } catch {
      // Ignore localStorage parse errors - use default folders
    }
  }, []);
  const [folderMode, setFolderMode] = useState<boolean>(true);

  // Hydrate folderMode from localStorage after mount to avoid SSR mismatch
  useEffect(() => {
    try {
      const v = localStorage.getItem(FOLDER_MODE_KEY);
      if (v === "flat") setFolderMode(false);
    } catch {
      // Ignore localStorage errors - use default folder mode
    }
  }, []);

  // ── Sidebar customization (reorder, hide, folders, usage tracking) ──
  const {
    customization, editMode, reorder, createFolder: createCustomFolder, deleteFolder,
    moveToFolder: moveItemToFolder, removeFromFolder, toggleHide, trackUsage,
    toggleAutoSort: handleToggleAutoSort, toggleEditMode, reset: resetSidebarCustom, setEditMode: _setEditMode,
    updateItem, clearItem,
    toggleSection, renameSection, createSection: createCustomSection, deleteSection, isSectionEmpty,
    moveItemToSection, removeItemFromSection: _removeItemFromSection, updateSection: updateSectionOverride,
  } = useSidebarCustomization(language);

  // Classic has no drag handles, so edit mode borrows the list view and hands
  // the tree back when it ends. Every render below reads this, not the stored mode.
  const viewMode: ViewMode = storedViewMode === "classic" && editMode ? "list" : storedViewMode;

  // Per-item edit popover state
  const [editingItemKey, setEditingItemKey] = useState<string | null>(null);
  // Section inline rename state
  const [renamingSectionId, setRenamingSectionId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const renameInputRef = useRef<HTMLInputElement>(null);
  // New section creation
  const [creatingSection, setCreatingSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const newSectionInputRef = useRef<HTMLInputElement>(null);

  // Collapsed sections set for quick lookup
  const collapsedSet = new Set(customization.collapsedSections);

  // Helper: resolve label for an item based on customization
  const resolveLabel = useCallback((itemKey: string, defaultLabel: string): string => {
    const ic = customization.itemCustomizations[itemKey];
    if (!ic?.labelLanguage) return defaultLabel;
    if (ic.labelLanguage === "custom" && ic.customLabel) return ic.customLabel;
    if (ic.labelLanguage === "custom") return defaultLabel;
    // For he/en/ru — get translations from that language
    const langTranslations = getTranslations(ic.labelLanguage);
    return ((langTranslations.tabs as Record<string, string>)?.[itemKey]) || defaultLabel;
  }, [customization.itemCustomizations]);

  // Helper: resolve icon for an item
  const resolveIcon = useCallback((itemKey: string, DefaultIcon: React.ElementType): React.ElementType => {
    const ic = customization.itemCustomizations[itemKey];
    if (!ic?.customIcon) return DefaultIcon;
    const custom = getIconByName(ic.customIcon);
    return custom || DefaultIcon;
  }, [customization.itemCustomizations]);

  // Helper: get icon position for an item (default to "start" before hydration to avoid mismatch)
  const getIconPosition = useCallback((itemKey: string): "start" | "end" | "above" => {
    if (!mounted) return "start";
    return customization.itemCustomizations[itemKey]?.iconPosition || "start";
  }, [customization.itemCustomizations, mounted]);


  // Context menu state
  const [ctxMenu, setCtxMenu] = useState<{
    x: number; y: number; itemKey: string; href: string; label: string;
  } | null>(null);

  // DnD
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const toggleFolder = useCallback((key: string) => {
    setOpenFolders((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try {
        localStorage.setItem("cc-sidebar-folders", JSON.stringify([...next]));
      } catch {
        // Ignore localStorage save errors - not critical for functionality
      }
      return next;
    });
  }, []);

  const handleFolderModeToggle = useCallback(() => {
    setFolderMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(FOLDER_MODE_KEY, next ? "folders" : "flat");
      } catch {
        // Ignore localStorage save errors - not critical for functionality
      }
      return next;
    });
  }, []);

  // navTop: compute position for workspace switcher dropdown
  useEffect(() => {
    if (navRef.current) {
      setNavTop(navRef.current.getBoundingClientRect().top - 8);
    }
  }, []);

  // Close social menu on outside click
  useEffect(() => {
    if (!socialMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (socialMenuRef.current && !socialMenuRef.current.contains(e.target as Node)) {
        setSocialMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [socialMenuOpen]);

  // Hydrate from localStorage + listen for cross-component favorites sync
  useEffect(() => {
    setFilter(loadFilter());
    setViewMode(loadViewMode());
    setFavHrefs(new Set(loadFavorites().map((f) => f.href)));

    const syncFavs = () => setFavHrefs(new Set(loadFavorites().map((f) => f.href)));
    window.addEventListener("cc-favorites-change", syncFavs);
    return () => window.removeEventListener("cc-favorites-change", syncFavs);
  }, []);

  const handleToggleFav = useCallback((href: string, label: string) => {
    const favs = loadFavorites();
    const idx = favs.findIndex((f) => f.href === href);
    if (idx >= 0) {
      favs.splice(idx, 1);
    } else {
      favs.push({ id: `fav-${Date.now()}`, href, label });
    }
    saveFavorites(favs);
    setFavHrefs(new Set(favs.map((f) => f.href)));
    window.dispatchEvent(new Event("cc-favorites-change"));
  }, []);

  // Use effectiveMode (from DashboardShell) when available — it accounts for mobile/tablet overrides.
  // Fall back to raw sidebarVisibility for backwards compatibility.
  const mode = effectiveMode ?? sidebarVisibility;
  const isFloat = mode === "float";
  // Hover-reveal: in visible mode with sidebarHoverMode, collapse to strip and expand on hover
  // When pinned, sidebar stays expanded and pushes content (not overlay)
  const isHoverReveal = mode === "visible" && sidebarHoverMode && !sidebarPinned && !isMobile;
  const isAppsView = viewMode === "apps";
  const isClassic = viewMode === "classic";
  const isCollapsed = isAppsView ? false : isFloat ? !hovered : isHoverReveal ? !hovered : false;
  const onRight = sidebarPosition === "right";
  const expandedWidth = isAppsView ? APPS_VIEW_WIDTH : isMobile ? MOBILE_WIDTH : (customWidth ?? FULL_WIDTH);

  // Resize drag state
  const [resizing, setResizing] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartW = useRef(expandedWidth);

  useEffect(() => {
    if (!resizing) return;
    const handleMove = (e: MouseEvent) => {
      const delta = onRight
        ? resizeStartX.current - e.clientX
        : e.clientX - resizeStartX.current;
      const newWidth = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, resizeStartW.current + delta));
      onWidthChange?.(newWidth);
    };
    const handleUp = () => setResizing(false);
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
  }, [resizing, onRight, onWidthChange]);

  const isHidden = mode === "hidden";
  const shouldCloseOnNav = isHidden && isFloating && onClose;
  const isTranslatedOff = isHidden && isFloating && !isOpen;
  const translateClass =
    isTranslatedOff && onRight
      ? "translate-x-full"
      : isTranslatedOff && !onRight
        ? "-translate-x-full"
        : "translate-x-0";

  // ── Filter + view handlers ────────────────────────────
  const handleFilterChange = (f: SidebarFilter) => {
    setFilter(f);
    try {
      localStorage.setItem(FILTER_KEY, f);
    } catch {
      // Ignore localStorage errors - not critical for functionality
    }
  };

  const handleViewModeChange = (v: ViewMode) => {
    setViewMode(v);
    try {
      localStorage.setItem(VIEW_MODE_KEY, v);
    } catch {
      // Ignore localStorage save errors - not critical for functionality
    }
  };

  // Leaving a scope releases its filter. Without this the strips go off screen
  // but keep filtering: pick a platform, switch to folders, and the menu is
  // still narrowed by a control nobody can see — which reads as apps having
  // disappeared. Each scope owns exactly one filter, so exit means reset.
  const handleScopeChange = (next: SidebarScope) => {
    if (next === scope) return;
    if (scope === "platforms") platformFilter.select(ALL_PLATFORMS);
    if (scope === "families") familyFilter.select(ALL_FAMILIES);
    if (scope === "folders") setSelectedAppFolderId(null);
    setScope(next);
  };

  // ── Compute filtered groups (uses customization engine) ─
  // referenceApp (Reference App / מילוני מערכת) label is sourced HERE, not in
  // src/lib/i18n/locales/*/tabs.ts — those are Track-1-modified files we must not
  // touch this session. Reconcile into tabs.ts once Track 1 lands. t.tabs wins if
  // it ever defines referenceApp (spread last).
  const tabsT = {
    referenceApp: language === "he" ? "מילוני מערכת" : "Reference App",
    ...(t.tabs as Record<string, string>),
  } as Record<string, string>;

  // Resolve the active tag/folder filter once. When set, the main nav (below)
  // and the apps view (further down) drop entries whose key isn't a member.
  // membership uses the existing "kind:ref" convention from useAppFolders:
  //   - "nav:<key>" for items registered in NAV_GROUPS
  //   - "app:<key>" for items registered through the App Launcher
  const activeAppFolder = selectedAppFolderId
    ? appFolders.find((f) => f.id === selectedAppFolderId) ?? null
    : null;
  const folderMemberKeys = activeAppFolder ? buildMemberKeySet(activeAppFolder) : null;

  const filteredGroups = buildDisplayGroups(
    NAV_GROUPS, customization, filter, favHrefs, permissions, editMode,
  ).map((dg) => {
    let items = dg.items;

    // Apply search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      items = items.flatMap((entry) => {
        if (isFolder(entry)) {
          // Check folder label
          const folderLabel = (resolveLabel(entry.key, tabsT[entry.key] || entry.key)).toLowerCase();
          // Filter children
          const matchingChildren = entry.children.filter((child) => {
            const childLabel = (resolveLabel(child.key, tabsT[child.key] || child.key)).toLowerCase();
            return childLabel.includes(q) || child.key.toLowerCase().includes(q) || matchesAppAlias(child.key, q);
          });
          // If folder itself matches, keep all children; otherwise keep only matching children
          if (folderLabel.includes(q)) return [entry];
          if (matchingChildren.length > 0) return [{ ...entry, children: matchingChildren }];
          return [];
        }
        const label = (resolveLabel(entry.key, tabsT[entry.key] || entry.key)).toLowerCase();
        return (label.includes(q) || entry.key.toLowerCase().includes(q) || matchesAppAlias(entry.key, q)) ? [entry] : [];
      });
    }

    // Apply tag/folder filter (multi-membership). Same matcher as the apps
    // view further down; runs here so the main nav reacts to the drawer too.
    if (folderMemberKeys) {
      items = items.flatMap((entry) => {
        if (isFolder(entry)) {
          const folderSelf =
            folderMemberKeys.has(`nav:${entry.key}`) || folderMemberKeys.has(`app:${entry.key}`);
          if (folderSelf) return [entry];
          const matchingChildren = entry.children.filter(
            (c) => folderMemberKeys.has(`nav:${c.key}`) || folderMemberKeys.has(`app:${c.key}`),
          );
          if (matchingChildren.length > 0) return [{ ...entry, children: matchingChildren }];
          return [];
        }
        return folderMemberKeys.has(`nav:${entry.key}`) || folderMemberKeys.has(`app:${entry.key}`)
          ? [entry]
          : [];
      });
    }

    // Apply the platform filter. Same shape as the folder filter above, but
    // keyed on href rather than on the item key: a platform is declared in
    // pm_apps against a route, so the route is what can be matched. A folder
    // whose own href does not match is kept when any child does, so a group
    // never vanishes while it still has something to show.
    // Both axes narrow the same list, so they run through one pass with the
    // predicates ANDed. Each fails open on its own, so a page nobody
    // classified survives both.
    if (platformFilter.isFiltering || familyFilter.isFiltering) {
      const keep = (href: string) => platformFilter.matches(href) && familyFilter.matches(href);
      items = items.flatMap((entry) => {
        if (isFolder(entry)) {
          const matchingChildren = entry.children.filter((c) => keep(c.href));
          if (matchingChildren.length > 0) return [{ ...entry, children: matchingChildren }];
          return keep(entry.href) ? [{ ...entry, children: [] }] : [];
        }
        return keep(entry.href) ? [entry] : [];
      });
    }

    return {
      id: dg.id,
      labelKey: dg.labelKey,
      isCustomFolder: dg.isCustomFolder,
      customFolderName: dg.customFolderName,
      isCustomSection: dg.isCustomSection,
      displayName: dg.displayName,
      emoji: dg.emoji,
      items,
    };
  }).filter((g) => g.items.length > 0
    || (!searchQuery.trim() && !folderMemberKeys && !platformFilter.isFiltering
        && !familyFilter.isFiltering));

  // DnD handler — reorder items within a group
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeId = active.id as string;
    const overId = over.id as string;

    // Case 1: Section reorder (both IDs are section: prefixed)
    if (activeId.startsWith("section:") && overId.startsWith("section:")) {
      const activeSectionId = activeId.replace("section:", "");
      const overSectionId = overId.replace("section:", "");
      const sectionIds = filteredGroups.map((g) => g.id);
      const oldIdx = sectionIds.indexOf(activeSectionId);
      const newIdx = sectionIds.indexOf(overSectionId);
      if (oldIdx !== -1 && newIdx !== -1) {
        const reordered = arrayMove(sectionIds, oldIdx, newIdx);
        reordered.forEach((id, i) => {
          updateSectionOverride(id, { sortOrder: i * 10 });
        });
      }
      return;
    }

    // Case 2: Item dropped onto a section header → move to that section
    if (!activeId.startsWith("section:") && overId.startsWith("section:")) {
      const targetSectionId = overId.replace("section:", "");
      const targetGroup = filteredGroups.find((g) => g.id === targetSectionId);
      if (targetGroup?.isCustomSection) {
        moveItemToSection(activeId, targetSectionId);
      }
      return;
    }

    // Case 3: Item-onto-item.
    // Find which group contains the SOURCE (active) and which contains the TARGET (over).
    let sourceGroup: typeof filteredGroups[number] | null = null;
    let targetGroup: typeof filteredGroups[number] | null = null;
    for (const group of filteredGroups) {
      const flatItemKeys = group.items.flatMap((e) =>
        isFolder(e) ? [e.key, ...e.children.map((c) => c.key)] : [e.key]
      );
      if (flatItemKeys.includes(activeId)) sourceGroup = group;
      if (flatItemKeys.includes(overId)) targetGroup = group;
    }
    if (!sourceGroup || !targetGroup) return;

    if (sourceGroup.id === targetGroup.id) {
      // Same-group reorder — original behavior
      const flatItemKeys = sourceGroup.items.flatMap((e) =>
        isFolder(e) ? [e.key, ...e.children.map((c) => c.key)] : [e.key]
      );
      const oldIdx = flatItemKeys.indexOf(activeId);
      const newIdx = flatItemKeys.indexOf(overId);
      if (oldIdx !== -1 && newIdx !== -1) {
        const newOrder = arrayMove(flatItemKeys, oldIdx, newIdx);
        reorder(sourceGroup.id, newOrder);
      }
      return;
    }

    // Cross-group move: drop activeId at overId's position in targetGroup,
    // and remove it from sourceGroup's order. buildDisplayGroups now honors
    // foreign keys appearing in another group's itemOrder ("claimedBy" map).
    const targetKeys = targetGroup.items.flatMap((e) =>
      isFolder(e) ? [e.key, ...e.children.map((c) => c.key)] : [e.key]
    );
    const targetIdx = targetKeys.indexOf(overId);
    const newTargetOrder = [...targetKeys];
    if (targetIdx !== -1) newTargetOrder.splice(targetIdx, 0, activeId);
    else newTargetOrder.push(activeId);
    reorder(targetGroup.id, newTargetOrder);

    // Also persist source group's order without the moved item — prevents
    // it from re-appearing if the user later resets target's order.
    const sourceKeys = sourceGroup.items
      .flatMap((e) => (isFolder(e) ? [e.key, ...e.children.map((c) => c.key)] : [e.key]))
      .filter((k) => k !== activeId);
    reorder(sourceGroup.id, sourceKeys);
  }, [filteredGroups, reorder, moveItemToSection, updateSectionOverride]);

  // Auto-open folder if a child page is active
  useEffect(() => {
    for (const group of NAV_GROUPS) {
      for (const entry of group.items) {
        if (isFolder(entry) && entry.children.some((c) => pathname === c.href || pathname.startsWith(c.href + "/"))) {
          setOpenFolders((prev) => {
            if (prev.has(entry.key)) return prev;
            const next = new Set(prev);
            next.add(entry.key);
            try {
              localStorage.setItem("cc-sidebar-folders", JSON.stringify([...next]));
            } catch {
              // Ignore localStorage save errors - not critical for functionality
            }
            return next;
          });
        }
      }
    }
  }, [pathname]);

  // ── Sliding active indicator measurement ─────────────
  // The indicator caches pixel top/height, so ANY layout change strands it:
  // text size, density, font, sidebar width, browser zoom. Listing every cause
  // in the dep array is a losing game (text scale was the one that caught us —
  // reported 2026-09-06), so a ResizeObserver on the nav re-measures whatever
  // moved. The dep array stays only for changes that swap the active element.
  useEffect(() => {
    if (isCollapsed || !navRef.current) {
      setIndicatorStyle(null);
      return;
    }
    const nav = navRef.current;

    const measure = () => {
      const activeEl = nav.querySelector('[data-active="true"]') as HTMLElement | null;
      if (!activeEl) {
        setIndicatorStyle(null);
        return;
      }
      const navRect = nav.getBoundingClientRect();
      const elRect = activeEl.getBoundingClientRect();
      setIndicatorStyle({
        top: elRect.top - navRect.top + nav.scrollTop,
        height: elRect.height,
      });
    };

    const frame = requestAnimationFrame(measure);
    const observer = new ResizeObserver(() => measure());
    observer.observe(nav);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [pathname, isCollapsed, filter, viewMode]);

  // ── Filter tab definitions ────────────────────────────
  const sidebarT = t.sidebar as Record<string, string>;

  // Edit popover labels
  const editLabels = {
    customLabel: sidebarT.customLabel || "Custom Label",
    language: sidebarT.language || "Display Language",
    iconPosition: sidebarT.iconPosition || "Icon Position",
    changeIcon: sidebarT.changeIcon || "Change Icon",
    reset: sidebarT.resetItem || "Reset",
    langHe: sidebarT.langHe || "עברית",
    langEn: sidebarT.langEn || "English",
    langRu: sidebarT.langRu || "Русский",
    langCustom: sidebarT.langCustom || "Custom",
    posStart: sidebarT.posStart || "Start",
    posEnd: sidebarT.posEnd || "End",
    posAbove: sidebarT.posAbove || "Above",
  };

  const filterTabs: { id: SidebarFilter; label: string }[] = [
    { id: "me", label: sidebarT.filterMe },
    { id: "team", label: sidebarT.filterTeam },
    { id: "hidden", label: sidebarT.filterHidden },
    { id: "favorites", label: sidebarT.filterFavorites },
  ];

  const viewModes: { id: ViewMode; icon: React.ElementType; label: string }[] = [
    { id: "list", icon: List, label: sidebarT.viewList },
    { id: "grid", icon: LayoutGrid, label: sidebarT.viewGrid },
    { id: "compact", icon: AlignJustify, label: sidebarT.viewCompact },
    { id: "apps", icon: CircleDot, label: sidebarT.viewApps || "Apps" },
    { id: "classic", icon: PanelLeft, label: language === "he" ? "קלאסי" : language === "ru" ? "Классический" : "Classic" },
  ];

  // ── Classic tree data — the same filtered groups the list renders, resolved
  // once into labels, icons and active state so SidebarClassicNav stays dumb.
  // Folders always nest here: a tree that flattens its folders is a list.
  const tabLabels = t.tabs as Record<string, string>;
  const toClassicEntry = (entry: NavEntry): ClassicEntry => {
    const isActive = entry.href === "/dashboard"
      ? pathname === "/dashboard"
      : isNavHrefActive(pathname, entry.href);
    return {
      key: entry.key,
      href: entry.href,
      label: resolveLabel(entry.key, tabLabels[entry.key] || entry.key),
      icon: resolveIcon(entry.key, entry.icon),
      isActive,
      isFav: favHrefs.has(entry.href),
      children: isFolder(entry) ? entry.children.map(toClassicEntry) : undefined,
    };
  };
  const classicSections: ClassicSection[] = isClassic
    ? filteredGroups.map((group) => ({
        id: group.id,
        label: group.displayName || group.customFolderName || sidebarT[group.labelKey] || group.id,
        emoji: group.emoji,
        collapsed: collapsedSet.has(group.id),
        entries: group.items.map(toClassicEntry),
      }))
    : [];
  const classicFavorites: ClassicEntry[] = [];
  if (isClassic) {
    const seen = new Set<string>();
    const walk = (entries: ClassicEntry[]) => {
      for (const e of entries) {
        if (e.isFav && !seen.has(e.href)) {
          seen.add(e.href);
          classicFavorites.push(e);
        }
        if (e.children) walk(e.children);
      }
    };
    classicSections.forEach((sec) => walk(sec.entries));
  }

  return (
    <aside
      data-cc-id="sidebar.root"
      data-info-id="sidebar"
      data-brand-spine=""
      className={`sidebar fixed shrink-0 overflow-hidden ${
        (isHidden && isOpen) || (isHoverReveal && hovered) ? "z-[45] shadow-2xl" : "z-[35]"
      } ${
        onRight ? "border-l" : "border-r"
      } border-white/[0.06] ${
        isFloat ? "shadow-lg" : ""
      } ${
        isHidden ? `transition-transform duration-300 [transition-timing-function:var(--spring-default)] ${translateClass}` : ""
      }`}
      style={{
        top: shellChromeTop(48),
        width: isCollapsed ? STRIP_WIDTH : expandedWidth,
        maxWidth: isMobile ? "100vw" : undefined,
        height: shellChromeHeight(48),
        ...(onRight ? { right: edgeOffset } : { left: 48 + edgeOffset }),
        transition: "width 300ms var(--spring-default)",
        // Brand v2 chrome: translucent pure-neutral background + macOS vibrancy.
        // Collapsed strip is slightly more transparent (60%), expanded is 72% to
        // keep nav labels readable against page content. See docs/BRAND.md.
        backgroundColor: isCollapsed
          ? "rgba(10, 10, 10, 0.60)"
          : "rgba(10, 10, 10, 0.72)",
        backdropFilter: "blur(40px) saturate(180%)",
        WebkitBackdropFilter: "blur(40px) saturate(180%)",
      }}
      onMouseEnter={(isFloat || isHoverReveal) ? () => setHovered(true) : undefined}
      onMouseLeave={(isFloat || isHoverReveal) ? () => setHovered(false) : undefined}
    >
      <div className="flex h-full flex-col">
        {/* Close button for floating sidebar (no header logo) */}
        {isFloating && !isFloat && onClose && (
          <div className="flex justify-end px-2 pt-2">
            <button type="button" onClick={onClose} className="btn-icon" aria-label="Close sidebar">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Pin button moved out of Sidebar header → use <SidebarPinButton />
            wherever you want the toggle (e.g., TopBar). Same shellPrefs state. */}

        {/* Workspace Switcher — replaces old user menu */}
        {user && !isAppsView && (
          <WorkspaceSwitcher
            user={user}
            isCollapsed={isCollapsed}
            onRight={onRight}
            expandedWidth={expandedWidth}
            navTop={navTop}
          />
        )}

        {/* ── 3-tab top mode switcher (expanded view only) ──
            1 = default nav  | 2 = extended (folders)  | 3 = widget settings */}
        {!isCollapsed && !isAppsView && (
          <div className="shrink-0 border-b border-white/[0.06] px-2 pt-2 pb-1.5">
            <div className="flex gap-0.5 rounded-lg bg-white/[0.03] border border-white/[0.06] p-0.5">
              {[
                { id: 1 as const, label: language === "he" ? "תפריט צד" : language === "ru" ? "Боковое" : "Sidebar" },
                { id: 3 as const, label: language === "he" ? "ווידג׳טים" : language === "ru" ? "Виджеты" : "Widgets" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => changeTopTab(tab.id)}
                  aria-pressed={topTab === tab.id}
                  title={`${tab.id}. ${tab.label}`}
                  className={`flex-1 rounded-md px-1.5 py-1 text-[0.625rem] font-medium transition-colors truncate ${
                    topTab === tab.id
                      ? "app-accent-soft-15 app-accent-text"
                      : "text-slate-500 hover:text-slate-300 hover:bg-white/[0.03]"
                  }`}
                >
                  <span className="opacity-50 me-1">{tab.id}</span>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tab 2 (folders) removed 2026-05-17 — folders now live in the
            far-right FoldersDrawer chrome surface. */}

        {/* ── Tab 3 stub: widget settings drill-down (macOS Finder columns style) ──
            Future: when a widget is open, show its settings hierarchy as
            navigable columns — deeper levels reveal nested config groups. */}
        {!isCollapsed && !isAppsView && topTab === 3 && (
          <SidebarWidgetInspector />
        )}

        {/* Filter tabs + View mode (hidden when collapsed or apps view OR not on tab 1) */}
        {!isCollapsed && !isAppsView && !isClassic && topTab === 1 && (
          <div className="shrink-0 border-b border-white/[0.06] px-2 pt-2 pb-1.5 space-y-1.5">
            {/* Scope toggle — one row picks which slice is on screen, and only
                that slice's strip renders beneath it. Replaced the platform and
                family selectors sitting permanently stacked here (2026-09-06). */}
            <SidebarScopeToggle
              scope={scope}
              onChange={handleScopeChange}
              language={language}
            />

            {scope === "platforms" && (
              <PlatformSelector filter={platformFilter} language={language} />
            )}
            {scope === "families" && (
              <FamilySelector filter={familyFilter} language={language} />
            )}
            {scope === "folders" && (
              <FolderStrip
                selectedFolderId={selectedAppFolderId}
                onSelect={setSelectedAppFolderId}
                onRight={onRight}
                labels={{
                  appFolders: sidebarT.appFolders || "תיקיות",
                  allApps: sidebarT.allApps || "All",
                  newAppFolder: sidebarT.newAppFolder || "תיקייה חדשה",
                  appFolderNamePlaceholder: sidebarT.appFolderNamePlaceholder || "שם...",
                  createFirstFolder: sidebarT.createFirstFolder || "צור תיקייה",
                }}
              />
            )}

            {/* Filter tabs */}
            <div data-cc-id="sidebar.filter-tabs" className="flex gap-0.5 rounded-lg bg-slate-800/50 p-0.5">
              {filterTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleFilterChange(tab.id)}
                  className={`flex-1 rounded-md px-1 py-1 text-[0.625rem] font-medium transition-colors truncate ${
                    filter === tab.id
                      ? "bg-[var(--cc-accent-600-20)] text-[var(--cc-accent-300)]"
                      : "text-slate-500 hover:text-slate-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Search bar */}
            <div className="relative">
              <Search className="absolute start-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-500 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === "he" ? "חיפוש בתפריט..." : "Search menu..."}
                className="w-full rounded-md border border-white/[0.06] bg-slate-800/50 py-1 ps-7 pe-7 text-[0.6875rem] text-slate-200 placeholder:text-slate-600 focus:border-[var(--cc-accent-500)] focus:outline-none"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute end-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-500 hover:text-slate-300"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>

            {/* View mode toggle + folder mode */}
            <div data-cc-id="sidebar.view-mode" className="flex items-center justify-center gap-0.5">
              {viewModes.map(({ id, icon: ModeIcon, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleViewModeChange(id)}
                  title={label}
                  className={`rounded p-1 transition-colors ${
                    viewMode === id
                      ? "text-[var(--cc-accent-300)] bg-[var(--cc-accent-600-20)]"
                      : "text-slate-600 hover:text-slate-400"
                  }`}
                >
                  <ModeIcon className="h-3.5 w-3.5" />
                </button>
              ))}
              <div className="mx-1 h-3 w-px bg-slate-700/50" />
              <button
                type="button"
                onClick={handleFolderModeToggle}
                title={folderMode ? (sidebarT.viewFlat || "Flat") : (sidebarT.viewFolders || "Folders")}
                className={`rounded p-1 transition-colors ${
                  folderMode
                    ? "text-[var(--cc-accent-300)] bg-[var(--cc-accent-600-20)]"
                    : "text-slate-600 hover:text-slate-400"
                }`}
              >
                {folderMode ? <ListTree className="h-3.5 w-3.5" /> : <FolderOpen className="h-3.5 w-3.5" />}
              </button>
              <div className="mx-1 h-3 w-px bg-slate-700/50" />
              <button
                type="button"
                onClick={toggleEditMode}
                title={editMode ? (sidebarT.doneEditing || "Done") : (sidebarT.editMode || "Customize")}
                className={`rounded p-1 transition-colors ${
                  editMode
                    ? "text-amber-400 bg-amber-400/10"
                    : "text-slate-600 hover:text-slate-400"
                }`}
              >
                {editMode ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
              </button>
            </div>

            {/* Edit mode toolbar */}
            {editMode && (
              <div className="flex items-center justify-center gap-1 pt-1">
                <button
                  type="button"
                  onClick={handleToggleAutoSort}
                  title={sidebarT.autoSort || "Sort by usage"}
                  className={`rounded px-1.5 py-0.5 text-[0.625rem] transition-colors ${
                    customization.autoSortByUsage
                      ? "text-[var(--cc-accent-300)] bg-[var(--cc-accent-600-20)]"
                      : "text-slate-600 hover:text-slate-400"
                  }`}
                >
                  <BarChart3 className="h-3 w-3 inline mr-0.5" />
                  {sidebarT.autoSort || "Usage"}
                </button>
                <button
                  type="button"
                  onClick={() => { if (confirm(sidebarT.resetConfirm || "Reset all customizations?")) resetSidebarCustom(); }}
                  title={sidebarT.resetOrder || "Reset"}
                  className="rounded px-1.5 py-0.5 text-[0.625rem] text-slate-600 hover:text-red-400 transition-colors"
                >
                  <RotateCcw className="h-3 w-3 inline mr-0.5" />
                  {sidebarT.resetOrder || "Reset"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Apps-only view — gradient icon strip */}
        {isAppsView && (() => {
          const activeAppFolder = selectedAppFolderId
            ? appFolders.find((f) => f.id === selectedAppFolderId)
            : null;
          // If user picked a folder that no longer exists (deleted), treat as "all"
          const folderMemberKeys = activeAppFolder ? buildMemberKeySet(activeAppFolder) : null;

          // Build filtered items using the same filter logic as main nav
          const appsFilteredItems = NAV_GROUPS.flatMap((group) =>
            group.items.flatMap((entry): NavItem[] => {
              if (isFolder(entry)) {
                return [
                  { href: entry.href, key: entry.key, icon: entry.icon, status: entry.status },
                  ...entry.children,
                ];
              }
              return [entry];
            })
          )
            // Apply filter
            .filter((item) => {
              if (filter === "hidden") return customization.hiddenItems.includes(item.key);
              if (filter === "favorites") return favHrefs.has(item.href);
              // "me" and "team" show non-hidden
              return !customization.hiddenItems.includes(item.key);
            })
            // Apply app-folder filter (multi-membership tag filter)
            .filter((item) => {
              if (!folderMemberKeys) return true;
              return folderMemberKeys.has(`nav:${item.key}`) || folderMemberKeys.has(`app:${item.key}`);
            })
            // Apply the platform filter (DEC-0362). Keyed on href, because a
            // platform is declared against a route in pm_apps.
            .filter((item) => platformFilter.matches(item.href) && familyFilter.matches(item.href))
            // Deduplicate by href
            .filter((item, idx, arr) => arr.findIndex((a) => a.href === item.href) === idx);

          return (
          <nav className="flex flex-1 flex-col overflow-hidden">
            {/* Top controls — compact filter + view toggle */}
            <div className="shrink-0 px-1.5 pt-2 pb-1 space-y-1.5">
              {/* Scope toggle — same control and same stored choice as the main
                  nav, so switching views does not switch what you were looking
                  at. Icons only: this rail is a third of the width. */}
              <SidebarScopeToggle
                scope={scope}
                onChange={handleScopeChange}
                language={language}
                variant="compact"
              />

              {scope === "platforms" && (
                <PlatformSelector filter={platformFilter} language={language} />
              )}
              {scope === "families" && (
                <FamilySelector filter={familyFilter} language={language} />
              )}

              {/* Filter icons row */}
              <div className="flex items-center justify-center">
                <div className="flex items-center gap-0.5 rounded-lg bg-slate-800/50 p-0.5">
                  {filterTabs.map((tab) => {
                    const filterIcons: Record<SidebarFilter, React.ElementType> = {
                      me: UserIcon,
                      team: Users,
                      hidden: EyeOff,
                      favorites: Star,
                    };
                    const FilterIcon = filterIcons[tab.id];
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => handleFilterChange(tab.id)}
                        title={tab.label}
                        className={`rounded-md p-1.5 transition-colors ${
                          filter === tab.id
                            ? "bg-[var(--cc-accent-600-20)] text-[var(--cc-accent-300)]"
                            : "text-slate-600 hover:text-slate-400"
                        }`}
                      >
                        <FilterIcon className="h-3 w-3" />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* View mode toggle */}
              <div className="flex items-center justify-center gap-0.5">
                {viewModes.map(({ id, icon: ModeIcon, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleViewModeChange(id)}
                    title={label}
                    className={`rounded p-1 transition-colors ${
                      viewMode === id
                        ? "text-[var(--cc-accent-300)] bg-[var(--cc-accent-600-20)]"
                        : "text-slate-600 hover:text-slate-400"
                    }`}
                  >
                    <ModeIcon className="h-3 w-3" />
                  </button>
                ))}
              </div>
            </div>

            {/* Recent items */}
            {recentItems.length > 0 && (
              <div className="px-1.5 pt-1.5 pb-0.5">
                <div className="flex items-center gap-1 px-1 mb-1">
                  <Clock className="h-2.5 w-2.5 text-slate-600" />
                  <span className="text-[0.5625rem] text-slate-600 font-medium">אחרונים</span>
                </div>
                <div className="space-y-0.5 max-h-36 overflow-y-auto scrollbar-none">
                  {recentItems.slice(0, 5).map((item) => (
                    <Link
                      key={item.id}
                      href={item.route}
                      onClick={() => { if (shouldCloseOnNav) onClose?.(); }}
                      className="group/recent flex items-center gap-1.5 rounded-md px-1.5 py-1 hover:bg-slate-800/60 transition-colors"
                    >
                      <span className="text-[0.625rem] shrink-0">{item.icon || "📄"}</span>
                      <span className="text-[0.625rem] text-slate-400 truncate flex-1">{item.title}</span>
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          await deleteRecentItem(item.id);
                          await queryClient.invalidateQueries({ queryKey: ["recent-items"] });
                        }}
                        className="opacity-0 group-hover/recent:opacity-100 shrink-0 rounded p-0.5 text-slate-700 hover:text-red-400 transition-all"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* App folders strip — tag-based, multi-membership. Now one scope
                among four rather than a permanent band, so the icon grid keeps
                the height the folders used to take when nothing was filtered. */}
            {scope === "folders" && (
              <>
                <div className="mx-2 divider-accent" />
                <FolderStrip
                  selectedFolderId={selectedAppFolderId}
                  onSelect={setSelectedAppFolderId}
                  onRight={onRight}
                  labels={{
                    appFolders: sidebarT.appFolders || "תיקיות",
                    allApps: sidebarT.allApps || "All",
                    newAppFolder: sidebarT.newAppFolder || "תיקייה חדשה",
                    appFolderNamePlaceholder: sidebarT.appFolderNamePlaceholder || "שם...",
                    createFirstFolder: sidebarT.createFirstFolder || "צור תיקייה",
                  }}
                />
              </>
            )}

            {/* Accent divider before the grid */}
            <div className="mx-2 divider-accent" />

            {/* App icons grid */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden px-1.5 py-2 scrollbar-none">
              <div className="flex flex-col items-center gap-2">
                {appsFilteredItems.length === 0 ? (
                  <div className="py-6 text-center">
                    <EyeOff className="h-4 w-4 mx-auto text-slate-700 mb-1" />
                    <span className="text-[0.5625rem] text-slate-700">{filter === "favorites" ? "★" : "—"}</span>
                  </div>
                ) : (
                  appsFilteredItems.map((item) => {
                    const isActive = item.href === "/dashboard"
                      ? pathname === "/dashboard"
                      : isNavHrefActive(pathname, item.href);
                    const gradient = getEffectiveGradient(item.key);
                    const [from, to] = gradient;
                    const itemLabel = resolveLabel(item.key, tabsT[item.key] || item.key);
                    const ItemIcon = resolveIcon(item.key, item.icon);
                    const appRecord = allApps.find((app) => app.route === item.href);

                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        data-launcher-id={`page:${item.key}`}
                        data-info-id="sidebar-nav-item"
                        data-context-target-uuid={appRecord?.id}
                        data-context-target-app-key={appRecord?.slug || item.key}
                        data-context-target-kind="application"
                        data-context-target-label={itemLabel}
                        data-context-target-href={item.href}
                        data-context-target-capabilities="app.open app.configure-actions"
                        aria-current={isActive ? "page" : undefined}
                        onClick={() => { trackUsage(item.key); if (shouldCloseOnNav) onClose?.(); }}
                        className={`group relative flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] transition-all duration-200 ${
                          isActive
                            ? "scale-110"
                            : "opacity-90 hover:opacity-100 hover:scale-108 hover:-translate-y-0.5"
                        } ${item.status === "coming-soon" ? "opacity-30 grayscale" : ""}`}
                        style={{
                          background: `linear-gradient(135deg, ${from}, ${to})`,
                          boxShadow: isActive
                            ? `0 6px 16px ${from}50, 0 0 0 2px rgba(255,255,255,0.15), inset 0 1px 0 rgba(255,255,255,0.2)`
                            : `0 2px 8px ${from}20`,
                        }}
                      >
                        {/* Gloss overlay */}
                        <div
                          className="pointer-events-none absolute inset-0 rounded-[14px]"
                          style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 45%)" }}
                        />
                        <ItemIcon className="relative z-10 h-[22px] w-[22px] text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" />
                        {/* Active indicator — bottom dot */}
                        {isActive && (
                          <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 h-1 w-4 rounded-full"
                            style={{ background: `linear-gradient(90deg, ${from}, ${to})`, boxShadow: `0 0 6px ${from}60` }}
                          />
                        )}
                        {/* Tooltip */}
                        <span
                          className={`absolute ${
                            onRight ? "right-full mr-3" : "left-full ml-3"
                          } rounded-lg px-2.5 py-1.5 text-xs font-medium text-white opacity-0 group-hover:opacity-100 transition-all duration-150 pointer-events-none whitespace-nowrap z-50 shadow-xl`}
                          style={{
                            background: `linear-gradient(135deg, ${from}ee, ${to}ee)`,
                            backdropFilter: "blur(8px)",
                          }}
                        >
                          {itemLabel}
                        </span>
                      </Link>
                    );
                  })
                )}
              </div>
            </div>

          </nav>
          );
        })()}

        {/* Nav with grouped items — only on tab 1 (or always when collapsed).
            Wrapped so the slash-menu plugins panel below can slide over it
            without touching the nav's own (large, DnD-heavy) internals. */}
        {isClassic && !isCollapsed && topTab === 1 && (
          <SidebarClassicNav
            sections={classicSections}
            favorites={classicFavorites}
            language={language}
            onRight={onRight}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onToggleSection={toggleSection}
            onNavigate={(key) => { trackUsage(key); if (shouldCloseOnNav) onClose?.(); }}
            onToggleFav={handleToggleFav}
            onMore={(e, entry) => {
              e.preventDefault();
              setCtxMenu({ x: e.clientX, y: e.clientY, itemKey: entry.key.replace(/^fav:/, ""), href: entry.href, label: entry.label });
            }}
            footer={
              <div data-cc-id="sidebar.classic.view-mode" className="flex items-center justify-center gap-0.5">
                {viewModes.map(({ id, icon: ModeIcon, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleViewModeChange(id)}
                    title={label}
                    aria-pressed={viewMode === id}
                    className={`rounded p-1 transition-colors ${
                      viewMode === id
                        ? "bg-[var(--cc-accent-600-20)] text-[var(--cc-accent-300)]"
                        : "text-[var(--fg-faint)] hover:text-[var(--fg-default)]"
                    }`}
                  >
                    <ModeIcon className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
            }
          />
        )}

        {!isAppsView && (isCollapsed || topTab === 1) && !(isClassic && !isCollapsed) && (
        <div className="relative min-h-0 flex-1 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            transform: slashMenu ? "translateX(100%)" : "translateX(0)",
            transition: "transform 300ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
        <nav
          ref={navRef}
          data-cc-id="sidebar.nav"
          className="relative h-full overflow-y-auto p-2"
        >
          {/* Sliding active indicator (list mode only) */}
          {!isCollapsed && viewMode === "list" && indicatorStyle && !editMode && (
            <div
              className="absolute inset-x-2 rounded-lg bg-[var(--cc-accent-600-20)] border border-[var(--cc-accent-500)]/20 pointer-events-none transition-all duration-300 ease-out"
              style={{
                top: indicatorStyle.top,
                height: indicatorStyle.height,
              }}
            />
          )}

         <DndContext
           id="sidebar-dnd"
           sensors={sensors}
           collisionDetection={closestCenter}
           onDragEnd={handleDragEnd}
           autoScroll={{ threshold: { x: 0, y: 0.15 }, interval: 5 }}
         >
          <SortableContext items={filteredGroups.map((g) => `section:${g.id}`)} strategy={verticalListSortingStrategy}>
          {filteredGroups.map((group, gi) => {
            // Collect all sortable IDs for this group (section header + items)
            const sortableIds = [`section:${group.id}`, ...group.items.flatMap((e) =>
              isFolder(e) ? [e.key, ...e.children.map((c) => c.key)] : [e.key]
            )];
            const isSectionCollapsed = collapsedSet.has(group.id);
            const sectionLabel = group.displayName || group.customFolderName || sidebarT[group.labelKey] || group.id;
            const sectionEmoji = group.emoji;
            const isRenamingThis = renamingSectionId === group.id;
            const canDelete = (group.isCustomSection || group.isCustomFolder) && isSectionEmpty(group.id);

            return (
            <SortableContext key={group.id} items={sortableIds} strategy={viewMode === "grid" ? rectSortingStrategy : verticalListSortingStrategy}>
            <SortableSectionDiv id={`section:${group.id}`} editMode={editMode}>
              {/* Group label / divider — now interactive toggle */}
              {gi > 0 && isCollapsed && (
                <hr className="divider mx-2 my-2" />
              )}
              {!isCollapsed && (viewMode === "list" || viewMode === "grid") && (
                <div className={`${gi === 0 ? "pt-1 pb-1" : "pt-3 pb-1"} ${viewMode === "grid" ? "px-1" : "px-2"}`}>
                  <div className={`group/section relative flex items-center gap-1 rounded-md px-1 py-0.5 -mx-1 transition-colors ${
                    editMode ? "hover:bg-slate-800/50" : "hover:bg-slate-800/30 cursor-pointer"
                  }`}>
                    {/* Decorative accent shape */}
                    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-md" aria-hidden>
                      <div className="absolute -right-3 top-1/2 -translate-y-1/2 h-6 w-12 rounded-full bg-gradient-to-l from-[var(--cc-accent-500)]/[0.04] to-transparent blur-sm" />
                    </div>
                    {/* Collapse chevron */}
                    <button
                      type="button"
                      onClick={() => toggleSection(group.id)}
                      className="shrink-0 rounded p-0.5 text-slate-600 hover:text-slate-400 transition-colors"
                    >
                      <ChevronRight className={`h-2.5 w-2.5 transition-transform duration-200 ${isSectionCollapsed ? "" : "rotate-90"}`} />
                    </button>

                    {/* Section name — clickable toggle or inline edit */}
                    {isRenamingThis ? (
                      <input
                        ref={renameInputRef}
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => {
                          if (renameValue.trim()) renameSection(group.id, renameValue.trim());
                          setRenamingSectionId(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            if (renameValue.trim()) renameSection(group.id, renameValue.trim());
                            setRenamingSectionId(null);
                          }
                          if (e.key === "Escape") setRenamingSectionId(null);
                        }}
                        className="flex-1 bg-transparent border-b border-[var(--cc-accent-500)] text-[0.625rem] uppercase tracking-wider text-slate-300 font-medium outline-none px-0.5"
                        autoFocus
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          if (editMode) {
                            setRenamingSectionId(group.id);
                            setRenameValue(sectionLabel);
                          } else {
                            toggleSection(group.id);
                          }
                        }}
                        className="flex-1 text-start"
                      >
                        <span className="text-[0.625rem] uppercase tracking-wider text-slate-600 font-medium">
                          {sectionEmoji ? `${sectionEmoji} ` : ""}{sectionLabel}
                        </span>
                      </button>
                    )}

                    {/* Item count badge */}
                    {isSectionCollapsed && (
                      <span className="text-[0.5625rem] text-slate-700 tabular-nums">{group.items.length}</span>
                    )}

                    {/* Edit mode: delete custom section */}
                    {editMode && canDelete && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (group.isCustomSection) deleteSection(group.id);
                          else if (group.isCustomFolder) deleteFolder(group.id);
                        }}
                        className="shrink-0 rounded p-0.5 text-slate-700 hover:text-red-400 transition-colors opacity-0 group-hover/section:opacity-100"
                        title={sidebarT.deleteSection || "Delete section"}
                      >
                        <Trash2 className="h-2.5 w-2.5" />
                      </button>
                    )}
                  </div>
                </div>
              )}
              {!isCollapsed && viewMode === "compact" && gi > 0 && (
                <div className="mx-1 my-1">
                  <div className={`group/section relative flex items-center gap-1 rounded-md px-1 py-0.5 transition-colors ${
                    editMode ? "hover:bg-slate-800/50" : "hover:bg-slate-800/30 cursor-pointer"
                  }`}>
                    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-md" aria-hidden>
                      <div className="absolute -right-3 top-1/2 -translate-y-1/2 h-5 w-10 rounded-full bg-gradient-to-l from-[var(--cc-accent-500)]/[0.04] to-transparent blur-sm" />
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleSection(group.id)}
                      className="shrink-0 rounded p-0.5 text-slate-600 hover:text-slate-400 transition-colors"
                    >
                      <ChevronRight className={`h-2.5 w-2.5 transition-transform duration-200 ${isSectionCollapsed ? "" : "rotate-90"}`} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (editMode) { setRenamingSectionId(group.id); setRenameValue(sectionLabel); }
                        else toggleSection(group.id);
                      }}
                      className="flex-1 text-start"
                    >
                      <span className="text-[0.5625rem] uppercase tracking-wider text-slate-600 font-medium">
                        {sectionEmoji ? `${sectionEmoji} ` : ""}{sectionLabel}
                      </span>
                    </button>
                    {isSectionCollapsed && (
                      <span className="text-[0.5625rem] text-slate-700 tabular-nums">{group.items.length}</span>
                    )}
                    {editMode && canDelete && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); if (group.isCustomSection) deleteSection(group.id); else if (group.isCustomFolder) deleteFolder(group.id); }}
                        className="shrink-0 rounded p-0.5 text-slate-700 hover:text-red-400 transition-colors opacity-0 group-hover/section:opacity-100"
                      >
                        <Trash2 className="h-2.5 w-2.5" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Items — collapse wrapper */}
              <div className={`transition-all duration-200 ease-out ${
                isSectionCollapsed && !isCollapsed ? "max-h-0 opacity-0 overflow-hidden" : "max-h-[2000px] opacity-100"
              } ${editingItemKey && sortableIds.includes(editingItemKey) ? "overflow-visible" : "overflow-hidden"}`}>

              {/* Items — Grid view */}
              {!isCollapsed && viewMode === "grid" ? (
                <div className="grid grid-cols-3 gap-1 px-1">
                  {group.items.map((entry) => {
                    // Flatten folders into grid — show folder + children as separate grid items
                    const flatItems: NavItem[] = isFolder(entry)
                      ? [{ href: entry.href, key: entry.key, icon: entry.icon, status: entry.status }, ...entry.children]
                      : [entry];
                    // Deduplicate by href (folder parent can share href with first child)
                    const seen = new Set<string>();
                    return flatItems
                      .filter(({ href: h, key: k }) => {
                        if (seen.has(h)) return false;
                        seen.add(h);
                        return !customization.hiddenItems.includes(k) || editMode;
                      })
                      .map(({ href, key: itemKey, icon: DefaultIcon }) => {
                      const isActive = href === "/dashboard" ? pathname === "/dashboard" : isNavHrefActive(pathname, href);
                      const rawLabel = (t.tabs as Record<string, string>)[itemKey];
                      const gridLabel = resolveLabel(itemKey, rawLabel);
                      const GridIcon = resolveIcon(itemKey, DefaultIcon);
                      const isFav = favHrefs.has(href);
                      const isItemHidden = customization.hiddenItems.includes(itemKey);
                      return (
                        <div key={href} className={`group/grid relative ${isItemHidden && editMode ? "opacity-30" : ""}`}
                          onContextMenu={(e) => { if (editMode) return; e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, itemKey, href, label: gridLabel }); }}
                          onClick={editMode ? (e) => { e.preventDefault(); e.stopPropagation(); setEditingItemKey(editingItemKey === itemKey ? null : itemKey); } : undefined}
                        >
                          <Link
                            href={editMode ? "#" : href}
                            onClick={(e) => {
                              if (editMode) { e.preventDefault(); return; }
                              trackUsage(itemKey); if (shouldCloseOnNav) onClose?.();
                            }}
                            data-active={isActive || undefined}
                            aria-current={isActive ? "page" : undefined}
                            className={`flex flex-col items-center gap-1 rounded-lg p-2 text-center transition-colors ${
                              isActive
                                ? "nav-item-active"
                                : "text-slate-400 hover:bg-slate-800/50 hover:text-slate-200"
                            } ${editMode ? "ring-1 ring-slate-700/50 ring-dashed" : ""}`}
                            style={isActive ? {
                              background: 'color-mix(in srgb, var(--app-accent) 18%, transparent)',
                              color: 'var(--app-accent)',
                            } : undefined}
                          >
                            <GridIcon className="h-4 w-4 shrink-0" />
                            <span className="text-[0.5625rem] leading-tight truncate w-full">{gridLabel}</span>
                          </Link>
                          {!editMode && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleToggleFav(href, gridLabel); }}
                            className={`absolute top-0.5 right-0.5 rounded p-0.5 transition-all ${
                              isFav
                                ? "text-amber-400 opacity-100"
                                : "text-slate-600 opacity-0 group-hover/grid:opacity-100 hover:text-amber-400"
                            }`}
                            aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
                          >
                            <Star className="h-2.5 w-2.5" fill={isFav ? "currentColor" : "none"} />
                          </button>
                          )}
                          {editMode && editingItemKey === itemKey && (
                            <ItemEditPopover
                              itemKey={itemKey}
                              currentLabel={rawLabel}
                              customization={customization.itemCustomizations[itemKey]}
                              onUpdate={(patch) => updateItem(itemKey, patch)}
                              onClear={() => clearItem(itemKey)}
                              onClose={() => setEditingItemKey(null)}
                              isRtl={language === "he"}
                              labels={editLabels}
                            />
                          )}
                        </div>
                      );
                    });
                  })}
                </div>
              ) : (
                /* Items — List & Compact views */
                <div className={viewMode === "compact" && !isCollapsed ? "space-y-0" : "space-y-0.5"}>
                  {/* Flatten folders when folderMode is off */}
                  {(folderMode
                    ? group.items
                    : group.items.flatMap((entry): NavEntry[] =>
                        isFolder(entry)
                          ? [{ href: entry.href, key: entry.key, icon: entry.icon, status: entry.status }, ...entry.children]
                          : [entry]
                      )
                  ).map((entry) => {
                    // ── Folder rendering (only when folderMode is on) ──
                    if (isFolder(entry)) {
                      const { href, key, icon: DefaultFolderIcon, children } = entry;
                      const rawFolderLabel = (t.tabs as Record<string, string>)[key];
                      const folderLabel = resolveLabel(key, rawFolderLabel);
                      const FolderIcon = resolveIcon(key, DefaultFolderIcon);
                      const isOpen = openFolders.has(key);
                      const isFolderActive = pathname === href || pathname.startsWith(href + "/");
                      const hasActiveChild = children.some((c) => pathname === c.href || pathname.startsWith(c.href + "/"));
                      const isHighlighted = isFolderActive || hasActiveChild;
                      const isFolderHidden = customization.hiddenItems.includes(key);

                      if (isCollapsed) {
                        if (isFolderHidden && !editMode) return null;
                        return (
                          <Link
                            key={key}
                            href={href}
                            data-active={isHighlighted || undefined}
                            aria-label={folderLabel}
                            className={`group relative flex w-full items-center justify-center rounded-lg p-2.5 transition-colors ${
                              isHighlighted
                                ? "nav-item-active text-[var(--cc-accent-300)]"
                                : "text-slate-400 hover:bg-slate-800/50 hover:text-slate-200"
                            } ${isFolderHidden ? "opacity-30" : ""}`}
                          >
                            {isHighlighted && (
                              <span className={`absolute top-1/2 -translate-y-1/2 h-4 w-0.5 rounded-full bg-[var(--cc-accent-500)] ${onRight ? "right-0" : "left-0"}`} aria-hidden />
                            )}
                            <FolderIcon className="h-4 w-4 shrink-0" />
                            <span className={`absolute ${onRight ? "right-full mr-2" : "left-full ml-2"} rounded-md bg-slate-800 border border-slate-700 px-2 py-1 text-xs text-slate-200 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50`}>
                              {folderLabel}
                            </span>
                          </Link>
                        );
                      }

                      return (
                        <SortableNavItem
                          key={key}
                          id={key}
                          editMode={editMode}
                          isHidden={isFolderHidden}
                          onToggleHide={() => toggleHide(key)}
                          onRight={onRight}
                          onEditClick={editMode ? () => setEditingItemKey(editingItemKey === key ? null : key) : undefined}
                        >
                        <div className={`group/item relative ${isFolderHidden && editMode ? "opacity-30" : ""}`}>
                          {/* Folder header — name navigates + opens, chevron toggles */}
                          <div className="flex items-center">
                            <Link
                              href={editMode ? "#" : href}
                              onClick={(e) => {
                                if (editMode) { e.preventDefault(); return; }
                                trackUsage(key);
                                setOpenFolders((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(key)) next.delete(key); else next.add(key);
                                  return next;
                                });
                                if (shouldCloseOnNav) onClose?.();
                              }}
                              data-active={isFolderActive || undefined}
                              className={`relative z-10 flex flex-1 items-center gap-3 rounded-lg ${viewMode === "compact" ? "px-2.5 py-1 text-xs" : "px-3 py-2 text-sm"} transition-all duration-150 ${
                                onRight ? "flex-row-reverse" : ""
                              } ${
                                isHighlighted
                                  ? "nav-item-active text-[var(--cc-accent-300)] font-medium"
                                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/30"
                              } ${editMode ? "ring-1 ring-slate-700/50 ring-dashed" : ""}`}
                            >
                              <FolderIcon className={`${viewMode === "compact" ? "h-3.5 w-3.5" : "h-[18px] w-[18px]"} shrink-0`} />
                              <span className={`flex-1 truncate ${onRight ? "text-right" : "text-left"}`}>{folderLabel}</span>
                            </Link>
                            <button
                              type="button"
                              onClick={() => toggleFolder(key)}
                              className={`${onRight ? "ml-0.5" : "mr-0.5"} btn-icon-sm hover:bg-slate-700/40 transition-colors`}
                              aria-label={isOpen ? "Collapse" : "Expand"}
                            >
                              <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
                            </button>
                          </div>
                          {/* Edit popover for folder */}
                          {editMode && editingItemKey === key && (
                            <ItemEditPopover
                              itemKey={key}
                              currentLabel={rawFolderLabel}
                              customization={customization.itemCustomizations[key]}
                              onUpdate={(patch) => updateItem(key, patch)}
                              onClear={() => clearItem(key)}
                              onClose={() => setEditingItemKey(null)}
                              isRtl={language === "he"}
                              labels={editLabels}
                            />
                          )}
                          {/* Folder children */}
                          <div
                            className={`overflow-hidden transition-all duration-200 ease-out ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
                          >
                            <div className={`${onRight ? "pr-3" : "pl-3"} ${viewMode === "compact" ? "" : "mt-0.5"}`}>
                              {children.map(({ href: cHref, key: cKey, icon: CIcon }) => {
                                const cActive = pathname === cHref || pathname.startsWith(cHref + "/");
                                const cLabel = (t.tabs as Record<string, string>)[cKey];
                                const cFav = favHrefs.has(cHref);
                                return (
                                  <div key={cHref} className="group/child relative">
                                    <Link
                                      href={cHref}
                                      onClick={shouldCloseOnNav ? onClose : undefined}
                                      data-active={cActive || undefined}
                                      className={`relative z-10 flex items-center gap-2.5 rounded-md ${viewMode === "compact" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-[0.8125rem]"} transition-colors ${
                                        onRight ? "flex-row-reverse" : ""
                                      } ${
                                        cActive
                                          ? "nav-item-active text-[var(--cc-accent-300)] font-medium"
                                          : "text-slate-500 hover:text-slate-300 hover:bg-slate-800/30"
                                      }`}
                                    >
                                      <CIcon className={`${viewMode === "compact" ? "h-3 w-3" : "h-3.5 w-3.5"} shrink-0`} />
                                      <span className={`flex-1 truncate ${onRight ? "text-right" : "text-left"}`}>{cLabel}</span>
                                    </Link>
                                    <button
                                      type="button"
                                      onClick={() => handleToggleFav(cHref, cLabel)}
                                      className={`absolute ${onRight ? "left-1" : "right-1"} top-1/2 -translate-y-1/2 z-20 rounded p-0.5 transition-all ${
                                        cFav
                                          ? "text-amber-400 opacity-100"
                                          : "text-slate-600 opacity-0 group-hover/child:opacity-100 hover:text-amber-400"
                                      }`}
                                      aria-label={cFav ? "Remove from favorites" : "Add to favorites"}
                                    >
                                      <Star className="h-2.5 w-2.5" fill={cFav ? "currentColor" : "none"} />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        </SortableNavItem>
                      );
                    }

                    // ── Regular item rendering ──
                    const { href, key, icon: Icon } = entry;
                    const isActive = href === "/dashboard" ? pathname === "/dashboard" : (pathname === href || pathname.startsWith(href + "/"));
                    const label = (t.tabs as Record<string, string>)[key];

                    // ── Collapsed item ──
                    if (isCollapsed) {
                      if (customization.hiddenItems.includes(key) && !editMode) return null;
                      const collapsedLabel = resolveLabel(key, label);
                      const CollapsedIcon = resolveIcon(key, Icon);
                      return (
                        <Link
                          key={href}
                          href={href}
                          onClick={() => { trackUsage(key); if (shouldCloseOnNav) onClose?.(); }}
                          onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, itemKey: key, href, label: collapsedLabel }); }}
                          data-active={isActive || undefined}
                          aria-label={collapsedLabel}
                          className={`group relative flex items-center justify-center rounded-lg p-2.5 transition-colors ${
                            isActive
                              ? "nav-item-active text-[var(--cc-accent-300)]"
                              : "text-slate-400 hover:bg-slate-800/50 hover:text-slate-200"
                          } ${customization.hiddenItems.includes(key) ? "opacity-30" : ""}`}
                        >
                          {isActive && (
                            <span
                              className={`absolute top-1/2 -translate-y-1/2 h-4 w-0.5 rounded-full bg-[var(--cc-accent-500)] ${
                                onRight ? "right-0" : "left-0"
                              }`}
                              aria-hidden
                            />
                          )}
                          <CollapsedIcon className={`h-4 w-4 shrink-0 transition-transform duration-150 ${
                            !isActive ? "group-hover:scale-110" : ""
                          }`} />
                          {/* Tooltip */}
                          <span
                            className={`absolute ${
                              onRight ? "right-full mr-2" : "left-full ml-2"
                            } rounded-md bg-slate-800 border border-slate-700 px-2 py-1 text-xs text-slate-200 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50`}
                          >
                            {collapsedLabel}
                          </span>
                        </Link>
                      );
                    }

                    // ── Compact item ──
                    if (viewMode === "compact") {
                      const isFav = favHrefs.has(href);
                      const isItemHidden = customization.hiddenItems.includes(key);
                      const compactLabel = resolveLabel(key, label);
                      const CompactIcon = resolveIcon(key, Icon);
                      return (
                        <SortableNavItem
                          key={key}
                          id={key}
                          editMode={editMode}
                          isHidden={isItemHidden}
                          onToggleHide={() => toggleHide(key)}
                          onRight={onRight}
                          onEditClick={editMode ? () => setEditingItemKey(editingItemKey === key ? null : key) : undefined}
                        >
                        <div className="group/item relative"
                          onContextMenu={(e) => { if (editMode) return; e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, itemKey: key, href, label: compactLabel }); }}
                        >
                          <Link
                            href={editMode ? "#" : href}
                            onClick={(e) => {
                              if (editMode) { e.preventDefault(); return; }
                              trackUsage(key); if (shouldCloseOnNav) onClose?.();
                            }}
                            data-active={isActive || undefined}
                            className={`relative z-10 flex items-center gap-2 rounded px-2.5 py-1 text-xs transition-colors ${
                              onRight ? "flex-row-reverse" : ""
                            } ${
                              isActive
                                ? "nav-item-active text-[var(--cc-accent-300)] font-medium"
                                : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/30"
                            } ${isItemHidden && editMode ? "line-through" : ""} ${editMode ? "ring-1 ring-slate-700/50 ring-dashed" : ""}`}
                          >
                            <CompactIcon className="h-3.5 w-3.5 shrink-0" />
                            <span className={`flex-1 truncate ${onRight ? "text-right" : "text-left"}`}>{compactLabel}</span>
                          </Link>
                          {!editMode && (
                          <button
                            type="button"
                            onClick={() => handleToggleFav(href, compactLabel)}
                            className={`absolute ${onRight ? "left-1" : "right-1"} top-1/2 -translate-y-1/2 z-20 rounded p-0.5 transition-all ${
                              isFav
                                ? "text-amber-400 opacity-100"
                                : "text-slate-600 opacity-0 group-hover/item:opacity-100 hover:text-amber-400"
                            }`}
                            aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
                          >
                            <Star className="h-2.5 w-2.5" fill={isFav ? "currentColor" : "none"} />
                          </button>
                          )}
                          {editMode && editingItemKey === key && (
                            <ItemEditPopover
                              itemKey={key}
                              currentLabel={label}
                              customization={customization.itemCustomizations[key]}
                              onUpdate={(patch) => updateItem(key, patch)}
                              onClear={() => clearItem(key)}
                              onClose={() => setEditingItemKey(null)}
                              isRtl={language === "he"}
                              labels={editLabels}
                            />
                          )}
                        </div>
                        </SortableNavItem>
                      );
                    }

                    // ── Expanded list item ──
                    const isFav = favHrefs.has(href);
                    const isItemHidden = customization.hiddenItems.includes(key);
                    const displayLabel = resolveLabel(key, label);
                    const ResolvedIcon = resolveIcon(key, Icon);
                    const iconPos = getIconPosition(key);
                    const isIconAbove = iconPos === "above";
                    const isIconEnd = iconPos === "end";
                    // For RTL: "start" means right side, "end" means left side
                    // For LTR: "start" means left side, "end" means right side
                    const showIconFirst = onRight ? isIconEnd : !isIconEnd;

                    return (
                      <SortableNavItem
                        key={key}
                        id={key}
                        editMode={editMode}
                        isHidden={isItemHidden}
                        onToggleHide={() => toggleHide(key)}
                        onRight={onRight}
                        onEditClick={editMode ? () => setEditingItemKey(editingItemKey === key ? null : key) : undefined}
                      >
                      <div className="group/item relative"
                        onContextMenu={(e) => {
                          if (editMode) return;
                          e.preventDefault();
                          setCtxMenu({ x: e.clientX, y: e.clientY, itemKey: key, href, label: displayLabel });
                        }}
                      >
                        <Link
                          href={editMode ? "#" : href}
                          onClick={(e) => {
                            if (editMode) { e.preventDefault(); return; }
                            trackUsage(key); if (shouldCloseOnNav) onClose?.();
                          }}
                          data-cc-id="sidebar.nav.link"
                          data-active={isActive || undefined}
                          className={`group relative z-10 flex ${isIconAbove ? "flex-col items-center gap-1" : "items-center gap-3"} rounded-lg px-3 py-2 text-sm transition-all duration-150 ${
                            isActive
                              ? "nav-item-active text-[var(--cc-accent-300)] font-medium"
                              : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/30"
                          } ${isItemHidden && editMode ? "line-through" : ""} ${editMode ? "ring-1 ring-slate-700/50 ring-dashed" : ""}`}
                        >
                          {isIconAbove ? (
                            <>
                              <ResolvedIcon className={`h-[18px] w-[18px] shrink-0 transition-transform duration-150 ${
                                !isActive ? "group-hover:scale-105" : ""
                              }`} />
                              <span data-cc-id="sidebar.nav.link.label" data-cc-text="true" className="text-center truncate w-full text-xs">{displayLabel}</span>
                            </>
                          ) : showIconFirst ? (
                            <>
                              <ResolvedIcon className={`h-[18px] w-[18px] shrink-0 transition-transform duration-150 ${
                                !isActive ? "group-hover:scale-105" : ""
                              }`} />
                              <span data-cc-id="sidebar.nav.link.label" data-cc-text="true" className={`flex-1 truncate ${onRight ? "text-right" : "text-left"}`}>{displayLabel}</span>
                              <ChatUnreadBadge href={href} />
                              <AppStatusBadge href={href} allApps={allApps} />
                            </>
                          ) : (
                            <>
                              <ChatUnreadBadge href={href} />
                              <AppStatusBadge href={href} allApps={allApps} />
                              <span data-cc-id="sidebar.nav.link.label" data-cc-text="true" className={`flex-1 truncate ${onRight ? "text-right" : "text-left"}`}>{displayLabel}</span>
                              <ResolvedIcon className={`h-[18px] w-[18px] shrink-0 transition-transform duration-150 ${
                                !isActive ? "group-hover:scale-105" : ""
                              }`} />
                            </>
                          )}
                        </Link>
                        {!editMode && (
                        <button
                          type="button"
                          onClick={() => handleToggleFav(href, displayLabel)}
                          className={`absolute ${onRight ? "left-2" : "right-2"} top-1/2 -translate-y-1/2 z-20 rounded p-0.5 transition-all ${
                            isFav
                              ? "text-amber-400 opacity-100"
                              : "text-slate-600 opacity-0 group-hover/item:opacity-100 hover:text-amber-400"
                          }`}
                          aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
                        >
                          <Star className="h-3 w-3" fill={isFav ? "currentColor" : "none"} />
                        </button>
                        )}
                        {/* Edit popover */}
                        {editMode && editingItemKey === key && (
                          <ItemEditPopover
                            itemKey={key}
                            currentLabel={label}
                            customization={customization.itemCustomizations[key]}
                            onUpdate={(patch) => updateItem(key, patch)}
                            onClear={() => clearItem(key)}
                            onClose={() => setEditingItemKey(null)}
                            isRtl={language === "he"}
                            labels={editLabels}
                          />
                        )}
                      </div>
                      </SortableNavItem>
                    );
                  })}
                </div>
              )}

              </div>{/* end collapse wrapper */}
            </SortableSectionDiv>
            </SortableContext>
          );
          })}

          {/* Add Section button (edit mode only) */}
          {editMode && !isCollapsed && (
            <div className="px-2 pt-2">
              {creatingSection ? (
                <div className="flex items-center gap-1 px-1">
                  <input
                    ref={newSectionInputRef}
                    type="text"
                    value={newSectionName}
                    onChange={(e) => setNewSectionName(e.target.value)}
                    onBlur={() => {
                      if (newSectionName.trim()) createCustomSection(newSectionName.trim());
                      setCreatingSection(false);
                      setNewSectionName("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (newSectionName.trim()) createCustomSection(newSectionName.trim());
                        setCreatingSection(false);
                        setNewSectionName("");
                      }
                      if (e.key === "Escape") { setCreatingSection(false); setNewSectionName(""); }
                    }}
                    placeholder={sidebarT.newSectionName || "Section name..."}
                    className="flex-1 bg-transparent border-b border-[var(--cc-accent-500)] text-[0.625rem] text-slate-300 font-medium outline-none px-0.5 py-0.5"
                    autoFocus
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreatingSection(true)}
                  className="flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-[0.625rem] text-slate-600 hover:text-[var(--cc-accent-300)] hover:bg-[var(--cc-accent-600-20)] transition-colors"
                >
                  <Plus className="h-3 w-3" />
                  {sidebarT.addSection || "Add Section"}
                </button>
              )}
            </div>
          )}

         </SortableContext>
         </DndContext>
        </nav>
        </div>

        {/* תוספים — slash-menu mirror panel. Same categorized list the
            floating popup shows; clicking an item here runs the same
            command as clicking it in the popup. Slides in from the
            opposite edge as the Apps nav slides out. */}
        <div
          data-gam-slash-menu-mirror
          className="absolute inset-0 overflow-y-auto p-2"
          style={{
            transform: slashMenu ? "translateX(0)" : "translateX(-100%)",
            transition: "transform 300ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
          aria-hidden={!slashMenu}
        >
          <div className="flex items-center justify-between px-1 pb-2">
            <span className="text-[0.625rem] font-semibold uppercase tracking-wider text-[var(--fg-muted)]">
              תוספים
            </span>
            {editMode && (
              <button
                type="button"
                onClick={() => setSlashOrder(resetSlashOrder())}
                title="איפוס סדר התוספים"
                className="rounded px-1 py-0.5 text-[0.625rem] text-[var(--fg-muted)] hover:text-[var(--error)] transition-colors"
              >
                <RotateCcw className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* המסך האינטרקטיבי — the four plugins actually used last, at the very
              top for "use it again". Recorded on every run from this panel and
              from the floating quick bar, so the strip reflects real use rather
              than a hand-picked favourites list. Hidden until something has
              been used, so a first-time menu isn't topped by an empty box. */}
          {(() => {
            const allItems = (slashMenu?.categories ?? []).flatMap((c) => c.items);
            // find(), not a Map: lucide-react exports a `Map` icon and this
            // file imports it, which shadows the global Map constructor.
            const recentItems = slashRecents
              .map((t) => allItems.find((i) => i.title === t))
              .filter((i): i is (typeof allItems)[number] => Boolean(i));
            if (recentItems.length === 0) return null;
            return (
              <div className="mb-2">
                <div className="px-2 py-1 text-[0.625rem] font-medium text-[var(--cc-accent-300)]">
                  לשימוש מהיר
                </div>
                <div className="flex flex-col gap-0.5">
                  {recentItems.map((item) => (
                    <button
                      key={`recent-${item.title}`}
                      type="button"
                      onClick={() => runSlashItem(item)}
                      className="flex items-center gap-2 rounded-md bg-[var(--cc-accent-600-20)]/40 px-2 py-1.5 text-start hover:bg-[var(--cc-accent-600-20)] transition-colors"
                      dir="rtl"
                    >
                      <span className="shrink-0 text-sm">{item.icon}</span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-medium text-[var(--fg-default)]">{item.titleHe}</div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mx-2 mt-2 h-px bg-[var(--border-hairline)]" />
              </div>
            );
          })()}

          {slashMenu && slashMenu.categories?.length === 0 && (
            <div className="px-2 py-6 text-center text-xs text-[var(--fg-muted)]">אין תוצאות</div>
          )}

          {/* Families, then plugins inside each — both in the user's own order.
              The arrows are rewritten from the list AS RENDERED, so a stored
              preference that mentions a plugin the catalog no longer has can
              never make an arrow move something invisible. */}
          {(() => {
            const cats = orderCategories(slashMenu?.categories ?? [], slashOrder);
            const catNames = cats.map((c) => c.name);
            return cats.map((cat, catIndex) => {
              const items = orderItems(cat.name, cat.items, slashOrder);
              const titles = items.map((i) => i.title);
              return (
                <div key={cat.name} className="mb-2">
                  <div className="flex items-center gap-1 px-2 py-1" dir="rtl">
                    <span className="min-w-0 flex-1 truncate text-[0.625rem] font-medium text-[var(--fg-muted)]">
                      {cat.nameHe}
                    </span>
                    {editMode && (
                      <span className="flex shrink-0 items-center gap-0.5">
                        <button
                          type="button"
                          disabled={catIndex === 0}
                          onClick={() => moveSlashCategory(catNames, cat.name, -1)}
                          title="העלה משפחה"
                          className="rounded p-0.5 text-[var(--fg-muted)] hover:text-[var(--warning)] disabled:opacity-25 transition-colors"
                        >
                          <ChevronUp className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          disabled={catIndex === cats.length - 1}
                          onClick={() => moveSlashCategory(catNames, cat.name, 1)}
                          title="הורד משפחה"
                          className="rounded p-0.5 text-[var(--fg-muted)] hover:text-[var(--warning)] disabled:opacity-25 transition-colors"
                        >
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {items.map((item, itemIndex) => (
                      /* dir="rtl", not "auto": `auto` resolves direction from the first
                         STRONG character, and an emoji is neutral. Emoji-led rows fell
                         through to the Hebrew label and rendered RTL, while the "H1"/
                         "H2"/"H3" rows resolved LTR — the same menu split down the
                         middle. This is Hebrew chrome, so the direction is pinned. */
                      <div key={item.title} className="flex items-center gap-1" dir="rtl">
                        <button
                          type="button"
                          onClick={() => runSlashItem(item)}
                          className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-start hover:bg-[var(--cc-accent-600-20)] transition-colors"
                          dir="rtl"
                        >
                          <span className="shrink-0 text-sm">{item.icon}</span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-xs font-medium text-[var(--fg-default)]">{item.titleHe}</div>
                            <div className="truncate text-[0.625rem] text-[var(--fg-faint)]">{item.description}</div>
                          </div>
                        </button>
                        {editMode && (
                          <span className="flex shrink-0 items-center gap-0.5 pl-1">
                            <button
                              type="button"
                              disabled={itemIndex === 0}
                              onClick={() => moveSlashItem(cat.name, titles, item.title, -1)}
                              title="העלה תוסף"
                              className="rounded p-0.5 text-[var(--fg-muted)] hover:text-[var(--warning)] disabled:opacity-25 transition-colors"
                            >
                              <ChevronUp className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              disabled={itemIndex === items.length - 1}
                              onClick={() => moveSlashItem(cat.name, titles, item.title, 1)}
                              title="הורד תוסף"
                              className="rounded p-0.5 text-[var(--fg-muted)] hover:text-[var(--warning)] disabled:opacity-25 transition-colors"
                            >
                              <ChevronDown className="h-3 w-3" />
                            </button>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            });
          })()}
        </div>
        </div>
        )}

        {/* Context menu */}
        {ctxMenu && (
          <SidebarContextMenu
            x={ctxMenu.x}
            y={ctxMenu.y}
            itemKey={ctxMenu.itemKey}
            href={ctxMenu.href}
            label={ctxMenu.label}
            isHidden={customization.hiddenItems.includes(ctxMenu.itemKey)}
            customFolders={customization.customFolders}
            isFavorite={favHrefs.has(ctxMenu.href)}
            onToggleFav={() => handleToggleFav(ctxMenu.href, ctxMenu.label)}
            onToggleHide={() => toggleHide(ctxMenu.itemKey)}
            onMoveToFolder={(fid) => moveItemToFolder(ctxMenu.itemKey, fid)}
            onRemoveFromFolder={() => removeFromFolder(ctxMenu.itemKey)}
            onCreateFolder={(name) => createCustomFolder(name, "core")}
            onClose={() => setCtxMenu(null)}
            isRtl={language === "he"}
            labels={{
              hide: sidebarT.hide || "Hide",
              show: sidebarT.show || "Show",
              openNewTab: sidebarT.openNewTab || "Open in new tab",
              addFav: sidebarT.addFav || "Add to favorites",
              removeFav: sidebarT.removeFav || "Remove from favorites",
              moveToFolder: sidebarT.moveToFolder || "Move to folder...",
              newFolder: sidebarT.newFolder || "New folder...",
              removeFromFolder: sidebarT.removeFromFolder || "Remove from folder",
              folderName: sidebarT.folderName || "Folder name",
              addNote: sidebarT.addNote || "Add note",
              editNote: sidebarT.editNote || "Edit note",
            }}
          />
        )}

        {/* Footer — Downloads + Social */}
        {!isCollapsed && !isAppsView && (
          <footer ref={socialMenuRef} data-cc-id="sidebar.footer" className="relative shrink-0 border-t border-white/[0.06] px-2 py-1.5 flex items-center gap-1">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("cc-show-download-reminder"))}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1 text-[0.625rem] font-medium text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-300 ${
                onRight ? "flex-row-reverse" : ""
              }`}
            >
              <Download className="h-3 w-3 shrink-0" />
              {(t.downloads as Record<string, string>).title}
            </button>
            <div className="h-3 w-px bg-slate-700/50" />
            <button
              type="button"
              onClick={() => setSocialMenuOpen((v) => !v)}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1 text-[0.625rem] font-medium text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-300 ${
                onRight ? "flex-row-reverse" : ""
              }`}
            >
              <Globe className="h-3 w-3 shrink-0" />
              {(t.downloads as Record<string, string>).gamOnline}
            </button>

            {/* Social links popup */}
            {socialMenuOpen && (
              <div
                className="absolute z-50 rounded-xl border border-slate-700 shadow-2xl overflow-hidden"
                style={{
                  backgroundColor: "var(--nav-bg)",
                  bottom: "100%",
                  marginBottom: 6,
                  ...(onRight ? { right: 0 } : { left: 0 }),
                  width: expandedWidth - 16,
                }}
              >
                <div className="px-3 py-2 border-b border-white/[0.06]">
                  <span className="text-[0.625rem] uppercase tracking-wider text-slate-600 font-medium">
                    {(t.downloads as Record<string, string>).gamOnline}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2 px-3 py-3">
                  {Object.entries(SOCIAL_LINKS).map(([name, href]) => (
                    <a
                      key={name}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setSocialMenuOpen(false)}
                      className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-300"
                      title={name}
                    >
                      <SocialIcon name={name} />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </footer>
        )}
        {isCollapsed && (
          <footer className="shrink-0 border-t border-white/[0.06] p-1 flex flex-col items-center gap-0.5">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("cc-show-download-reminder"))}
              className="group relative rounded p-1.5 text-slate-600 transition-colors hover:bg-slate-800 hover:text-slate-400"
              title={(t.downloads as Record<string, string>).title}
            >
              <Download className="h-3.5 w-3.5" />
              <span
                className={`absolute ${
                  onRight ? "right-full mr-2" : "left-full ml-2"
                } rounded-md bg-slate-800 border border-slate-700 px-2 py-1 text-xs text-slate-200 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50`}
              >
                {(t.downloads as Record<string, string>).title}
              </span>
            </button>
          </footer>
        )}
      </div>

      {/* Resize handle */}
      {!isMobile && !isCollapsed && !isAppsView && (
        <div
          className={`absolute top-0 bottom-0 w-1 cursor-col-resize transition-colors hover:bg-[var(--cc-accent-500)]/30 active:bg-[var(--cc-accent-500)]/50 ${
            onRight ? "left-0" : "right-0"
          } ${resizing ? "bg-[var(--cc-accent-500)]/50" : ""}`}
          onMouseDown={(e) => {
            e.preventDefault();
            resizeStartX.current = e.clientX;
            resizeStartW.current = expandedWidth;
            setResizing(true);
          }}
        />
      )}
    </aside>
  );
}
